import React, { useEffect, useRef, useState } from 'react';
import {
    AlertCircle,
    ArrowLeft,
    ArrowRight,
    Award,
    BookOpen,
    Brain,
    Calendar,
    Camera,
    CheckCircle,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Clock,
    CloudSnow,
    Download,
    FileText,
    Heart,
    Home,
    Info,
    Menu,
    MessageSquare,
    Printer,
    RefreshCw,
    Star,
    User,
    Users,
    X,
    XCircle,
    Zap
} from 'lucide-react';

import { motion } from "framer-motion";
import Layout from "../Layout";
import { API_CONFIG } from "../config/api";

// Import PDF generation and styling components
import { addReportStyles, PreviewPageWrapper, ReportPageWrapper } from './ReportStyles';
import { generatePDF } from './PDFGeneration';


const API_BASE_URL = API_CONFIG.BASE_URL;

const domains = [
    "Physical Development",
    "Socio-Emotional and Ethical Development",
    "Cognitive Development",
    "Language and Literacy Development",
    "Aesthetic and Cultural Development",
];

// Report generator step status enum
const Status = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    SUCCESS: 'success',
    FAILED: 'failed'
};


const GraphPaperBackground = () => (
    <svg className="absolute inset-0 w-full h-full z-0 opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
        <pattern id="graph-paper" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-400" />
        </pattern>
        <rect x="0" y="0" width="100" height="100" fill="url(#graph-paper)" />
    </svg>
);

// Keep the rest of your existing SVG components...
const HangingChildSVG = () => (
    <svg viewBox="0 0 100 150" className="absolute top-0 right-0 w-32 h-40">
        <g transform="translate(50, 30)">
            {/* Head */}
            <circle cx="0" cy="0" r="15" fill="#FCA5A5" />
            {/* Face */}
            <circle cx="-5" cy="-2" r="1.5" fill="#1E3A8A" />
            {/* Left eye */}
            <circle cx="5" cy="-2" r="1.5" fill="#1E3A8A" />
            {/* Right eye */}
            <path d="M-5,7 Q0,10 5,7" fill="none" stroke="#1E3A8A" strokeWidth="1.5" />
            {/* Smile */}
            {/* Hair */}
            <path d="M-14,-5 Q-10,-15 0,-15 Q10,-15 14,-5" fill="#5B21B6" />
            {/* T-shirt */}
            <path d="M-15,0 L-15,30 L15,30 L15,0" fill="#EAB308" />
            {/* Arms */}
            <path d="M-15,10 L-25,20" fill="none" stroke="#FCA5A5" strokeWidth="8" strokeLinecap="round" />
            <path d="M15,10 L30,5" fill="none" stroke="#FCA5A5" strokeWidth="8" strokeLinecap="round" />
            {/* Legs */}
            <path d="M-8,30 L-12,50" fill="none" stroke="#FCA5A5" strokeWidth="8" strokeLinecap="round" />
            <path d="M8,30 L12,50" fill="none" stroke="#FCA5A5" strokeWidth="8" strokeLinecap="round" />
        </g>
    </svg>
);

const ChildIllustrationSVG = () => (
    <svg viewBox="0 0 80 80" className="w-16 h-16">
        <g transform="translate(40, 25)">
            {/* Head */}
            <circle cx="0" cy="0" r="15" fill="#FCA5A5" />
            {/* Face */}
            <circle cx="-5" cy="-2" r="1.5" fill="#1E3A8A" />
            {/* Left eye */}
            <circle cx="5" cy="-2" r="1.5" fill="#1E3A8A" />
            {/* Right eye */}
            <path d="M-5,7 Q0,10 5,7" fill="none" stroke="#1E3A8A" strokeWidth="1.5" />
            {/* Smile */}
            {/* Hair */}
            <path d="M-15,-5 Q-10,-18 0,-18 Q10,-18 15,-5" fill="#7C3AED" />
            {/* Body */}
            <path d="M-10,15 L-8,35 M8,35 L10,15" fill="none" stroke="#FCA5A5" strokeWidth="5" />
            {/* Arms */}
            <path d="M-10,15 L-18,25" fill="none" stroke="#FCA5A5" strokeWidth="5" />
            <path d="M10,15 L18,25" fill="none" stroke="#FCA5A5" strokeWidth="5" />
            {/* Clothing */}
            <path d="M-10,15 L0,25 L10,15" fill="#EAB308" />
        </g>
    </svg>
);


const BasketballSVG = () => (
    <svg viewBox="0 0 100 100" className="w-20 h-20">
        <circle cx="50" cy="50" r="40" fill="#F97316" />
        <path d="M10,50 L90,50" fill="none" stroke="#000" strokeWidth="2" />
        <path d="M50,10 L50,90" fill="none" stroke="#000" strokeWidth="2" />
        <path d="M20,20 Q50,30 80,20" fill="none" stroke="#000" strokeWidth="2" />
        <path d="M20,80 Q50,70 80,80" fill="none" stroke="#000" strokeWidth="2" />
    </svg>
);


const CBSELogoSVG = () => (
    <svg viewBox="0 0 100 100" className="w-16 h-16">
        <circle cx="50" cy="50" r="45" fill="#FFF" stroke="#000" strokeWidth="2" />
        <circle cx="50" cy="50" r="38" fill="none" stroke="#000" strokeWidth="1" />
        <path d="M50,20 L50,80" stroke="#000" strokeWidth="1" />
        <path d="M20,50 L80,50" stroke="#000" strokeWidth="1" />
        <path d="M30,30 L70,70" stroke="#000" strokeWidth="1" />
        <path d="M30,70 L70,30" stroke="#000" strokeWidth="1" />
        <circle cx="50" cy="50" r="10" fill="#FBBF24" />
        <path d="M50,30 L53,40 L63,40 L55,46 L58,56 L50,50 L42,56 L45,46 L37,40 L47,40 Z" fill="#1E3A8A" />
        <text x="50" y="85" textAnchor="middle" fontSize="8" fontWeight="bold">CBSE</text>
    </svg>
);

const AmritMahotsavSVG = () => (
    <svg viewBox="0 0 160 40" className="w-32 h-8">
        <text x="5" y="15" fontSize="12" fontWeight="bold" fill="#6B7280">75</text>
        <line x1="20" y1="10" x2="50" y2="10" stroke="#F97316" strokeWidth="2" />
        <line x1="20" y1="15" x2="50" y2="15" stroke="#22C55E" strokeWidth="2" />
        <line x1="20" y1="20" x2="50" y2="20" stroke="#3B82F6" strokeWidth="2" />
        <text x="55" y="15" fontSize="10" fontWeight="bold" fill="#1E3A8A">Azadi</text>
        <text x="55" y="25" fontSize="8" fill="#1E3A8A">ka</text>
        <text x="70" y="25" fontSize="10" fontWeight="bold" fill="#1E3A8A">Amrit Mahotsav</text>
    </svg>
);

const HappyChildSVG = () => (
    <svg viewBox="0 0 100 120" className="w-32 h-40">
        <g transform="translate(50, 40)">
            {/* Head */}
            <circle cx="0" cy="0" r="25" fill="#FCA5A5" />
            {/* Face */}
            <path d="M-10,0 C-8,8 8,8 10,0" stroke="#1E3A8A" strokeWidth="1.5" fill="none" />
            {/* Smile */}
            <path d="M-10,-5 C-15,-10 -10,-15 -5,-10" stroke="#1E3A8A" strokeWidth="1.5" fill="none" />
            {/* Left eye */}
            <path d="M10,-5 C15,-10 10,-15 5,-10" stroke="#1E3A8A" strokeWidth="1.5" fill="none" />
            {/* Right eye */}
            <circle cx="-12" cy="0" r="3" fill="#EF4444" />
            {/* Cheek */}
            <circle cx="12" cy="0" r="3" fill="#EF4444" />
            {/* Cheek */}

            {/* Hair */}
            <path d="M-20,-15 Q-15,-35 0,-30 Q15,-35 20,-15" fill="#7C3AED" />

            {/* Body - Life Ring */}
            <circle cx="0" cy="30" r="20" fill="#FBBF24" />
            <circle cx="0" cy="30" r="12" fill="#60A5FA" />

            {/* Arms */}
            <path d="M-15,15 L-35,0" fill="none" stroke="#FCA5A5" strokeWidth="12" strokeLinecap="round" />
            <path d="M15,15 L35,0" fill="none" stroke="#FCA5A5" strokeWidth="12" strokeLinecap="round" />
        </g>
    </svg>
);

const HappyChildWithPigtailsSVG = () => (
    <svg viewBox="0 0 100 120" className="w-32 h-40">
        <g transform="translate(50, 40)">
            {/* Head */}
            <circle cx="0" cy="0" r="25" fill="#FCA5A5" />
            {/* Face */}
            <path d="M-10,0 C-8,8 8,8 10,0" stroke="#1E3A8A" strokeWidth="1.5" fill="none" />
            {/* Smile */}
            <path d="M-10,-5 C-15,-10 -10,-15 -5,-10" stroke="#1E3A8A" strokeWidth="1.5" fill="none" />
            {/* Left eye */}
            <path d="M10,-5 C15,-10 10,-15 5,-10" stroke="#1E3A8A" strokeWidth="1.5" fill="none" />
            {/* Right eye */}
            <circle cx="-12" cy="0" r="3" fill="#EF4444" />
            {/* Cheek */}
            <circle cx="12" cy="0" r="3" fill="#EF4444" />
            {/* Cheek */}

            {/* Hair & Pigtails */}
            <path d="M-20,-15 Q-15,-35 0,-28 Q15,-35 20,-15" fill="#4B5563" />
            <path d="M-20,-15 Q-25,-5 -30,15 Q-35,25 -40,20" fill="#4B5563" />
            {/* Left pigtail */}
            <path d="M20,-15 Q25,-5 30,15 Q35,25 40,20" fill="#4B5563" />
            {/* Right pigtail */}

            {/* Body - Pink Dress */}
            <path d="M-20,15 L0,50 L20,15" fill="#EC4899" />
            <rect x="-20" y="10" width="40" height="10" fill="#EC4899" />

            {/* Arms */}
            <path d="M-15,15 L-35,0" fill="none" stroke="#FCA5A5" strokeWidth="12" strokeLinecap="round" />
            <path d="M15,15 L35,0" fill="none" stroke="#FCA5A5" strokeWidth="12" strokeLinecap="round" />
        </g>
    </svg>
);

const GuitarSVG = () => (
    <svg viewBox="0 0 100 200" className="w-24 h-48">
        <g transform="translate(50, 100)">
            {/* Guitar Body */}
            <ellipse cx="0" cy="40" rx="30" ry="50" fill="#FBBF24" />
            <ellipse cx="0" cy="30" rx="10" ry="10" fill="#000" />
            {/* Neck */}
            <rect x="-5" y="-90" width="10" height="120" fill="#92400E" />
            {/* Head */}
            <rect x="-10" y="-110" width="20" height="20" fill="#92400E" />
            {/* Tuning pegs */}
            <circle cx="-7" cy="-105" r="2" fill="#000" />
            <circle cx="7" cy="-105" r="2" fill="#000" />
            <circle cx="-7" cy="-95" r="2" fill="#000" />
            <circle cx="7" cy="-95" r="2" fill="#000" />
            {/* Strings */}
            <line x1="-3" y1="-90" x2="-3" y2="30" stroke="#FFF" strokeWidth="0.5" />
            <line x1="0" y1="-90" x2="0" y2="30" stroke="#FFF" strokeWidth="0.5" />
            <line x1="3" y1="-90" x2="3" y2="30" stroke="#FFF" strokeWidth="0.5" />
            {/* Frets */}
            <line x1="-5" y1="-80" x2="5" y2="-80" stroke="#FFF" strokeWidth="1" />
            <line x1="-5" y1="-70" x2="5" y2="-70" stroke="#FFF" strokeWidth="1" />
            <line x1="-5" y1="-60" x2="5" y2="-60" stroke="#FFF" strokeWidth="1" />
            <line x1="-5" y1="-50" x2="5" y2="-50" stroke="#FFF" strokeWidth="1" />
            <line x1="-5" y1="-40" x2="5" y2="-40" stroke="#FFF" strokeWidth="1" />
            <line x1="-5" y1="-30" x2="5" y2="-30" stroke="#FFF" strokeWidth="1" />
            <line x1="-5" y1="-20" x2="5" y2="-20" stroke="#FFF" strokeWidth="1" />
            <line x1="-5" y1="-10" x2="5" y2="-10" stroke="#FFF" strokeWidth="1" />
        </g>
    </svg>
);

const ABCSVGs = () => (
    <svg viewBox="0 0 100 40" className="w-24 h-10">
        <g transform="translate(10, 20)">
            <path d="M0,15 L10,0 L20,15 M5,8 L15,8" fill="none" stroke="#3B82F6" strokeWidth="3" />
        </g>
        <g transform="translate(40, 20)">
            <path d="M0,0 C0,0 0,15 10,15 C20,15 20,0 20,0 C20,0 20,15 10,15 C0,15 0,0 0,0 Z" fill="none"
                stroke="#EC4899" strokeWidth="3" />
        </g>
        <g transform="translate(70, 20)">
            <path d="M20,0 C20,0 20,15 10,15 C0,15 0,7.5 10,7.5 C20,7.5 20,15 10,15" fill="none" stroke="#FBBF24"
                strokeWidth="3" />
        </g>
    </svg>
);


// New animated backgrounds and decorative elements
const AnimatedDots = () => (
    <div className="absolute inset-0 z-0 overflow-hidden opacity-10">
        {Array.from({ length: 20 }).map((_, i) => (
            <div
                key={i}
                className="absolute rounded-full bg-primary-500"
                style={{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    width: `${Math.random() * 20 + 5}px`,
                    height: `${Math.random() * 20 + 5}px`,
                    opacity: Math.random() * 0.5 + 0.1,
                    animation: `float ${Math.random() * 10 + 10}s infinite ease-in-out`,
                    animationDelay: `${Math.random() * 5}s`
                }}
            />
        ))}
    </div>
);


const DecorativeAsset = ({ children, className = "" }) => (
    <motion.div
        className={`absolute ${className} z-20`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        whileHover={{ scale: 1.1, rotate: 2 }}
    >
        {children}
    </motion.div>
);


// 2. The main HolisticProgressCardPage with adjusted styles
const HolisticProgressCardPage = ({ forPdfRender = false }) => {
    // Root container: white for PDF, gradient on screen
    const containerBg = forPdfRender
        ? "bg-white"
        : "bg-gradient-to-br from-yellow-50 to-yellow-100";

    return (
        <div className={`relative w-full h-full ${containerBg}`}>
            {/* ————————————— BACKGROUNDS ————————————— */}
            {!forPdfRender && (
                <>
                    {/* Graph paper grid, light */}
                    <div
                        className="absolute inset-0 no-pdf-render"
                        style={{
                            backgroundImage:
                                "linear-gradient(rgba(59, 130, 246, 0.05) 1px, transparent 1px), " +
                                "linear-gradient(90deg, rgba(59, 130, 246, 0.05) 1px, transparent 1px)",
                            backgroundSize: "40px 40px",
                            opacity: 0.1,
                        }}
                    />

                    {/* Subtle swooshes */}
                    <div
                        className="absolute top-1/4 right-0 w-1/2 h-2/3 no-pdf-render bg-blue-200 rounded-l-full opacity-20" />
                    <div
                        className="absolute bottom-0 left-0 w-1/2 h-1/3 no-pdf-render bg-blue-200 rounded-tr-full opacity-20" />
                </>
            )}

            {/* ————————————— CONTENT LAYER ————————————— */}
            <div className="relative flex flex-col h-full pt-8 pb-16 px-10">
                {/* Logos */}
                <div className="flex justify-center md:justify-between items-center mb-6 md:mb-8">
                    <div className="w-32 md:w-36">
                        <AmritMahotsavSVG />
                    </div>
                    <div className="w-20 md:w-24">
                        <CBSELogoSVG />
                    </div>
                </div>

                {/* Center column */}
                <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                    {/* Year badge */}
                    <div
                        className={`${forPdfRender ? "px-8 py-2" : "px-6 py-1"
                            } bg-amber-200/90 shadow-lg rounded-full mb-6 transform rotate-[-2deg]`}
                    >
                        <h2
                            className={`font-bold tracking-widest text-gray-800 ${forPdfRender ? "text-lg" : "text-md md:text-lg"
                                }`}
                        >
                            2 0 2 3 – 2 0 2 4
                        </h2>
                    </div>

                    {/* Main title */}
                    <div className="space-y-1 mb-6">
                        <h1
                            className={`font-extrabold tracking-wide ${forPdfRender ? "text-6xl" : "text-4xl md:text-5xl"
                                } text-red-500`}
                        >
                            HOLISTIC
                        </h1>
                        <h1
                            className={`font-extrabold tracking-wide ${forPdfRender ? "text-6xl" : "text-4xl md:text-5xl"
                                } text-blue-700`}
                        >
                            PROGRESS
                        </h1>
                        <h1
                            className={`font-extrabold tracking-wide ${forPdfRender ? "text-6xl" : "text-4xl md:text-5xl"
                                } text-blue-700`}
                        >
                            CARD
                        </h1>
                    </div>

                    {/* Subtitle */}
                    <p
                        className={`italic text-gray-700 mb-6 ${forPdfRender ? "text-base" : "text-sm md:text-base"
                            }`}
                    >
                        (Adapted from PARAKH, NCERT prototype of HPC)
                    </p>

                    {/* Footer text */}
                    <div
                        className={`font-semibold ${forPdfRender ? "text-lg" : "text-sm md:text-base"
                            } text-blue-900`}
                    >
                        <p>Centre for Excellence in Assessment</p>
                        <p>CBSE</p>
                    </div>
                </div>

                {/* Foundational Stage badge */}
                {!forPdfRender && (
                    <div className="text-center mt-4">
                        <div
                            className="inline-block bg-gradient-to-r from-blue-100 to-blue-200 px-5 py-2 rounded-lg shadow">
                            <p className="font-bold text-blue-800 text-base">
                                Foundational Stage
                            </p>
                            <p className="text-blue-800 text-sm">Age group 3 – 6 years</p>
                        </div>
                    </div>
                )}
            </div>

            {/* ————————————— DECORATIVE CUT-OUTS ————————————— */}
            {!forPdfRender && (
                <>
                    <DecorativeAsset className="absolute bottom-14 left-6 w-24 md:w-28">
                        <HappyChildWithPigtailsSVG />
                    </DecorativeAsset>

                    <DecorativeAsset className="absolute right-12 top-64 w-24 md:w-28">
                        <HappyChildSVG />
                    </DecorativeAsset>

                    <DecorativeAsset className="absolute bottom-20 left-1/4 w-20 md:w-24">
                        <BasketballSVG />
                    </DecorativeAsset>

                    <DecorativeAsset className="absolute right-12 bottom-14 w-16 md:w-20">
                        <GuitarSVG />
                    </DecorativeAsset>

                    <DecorativeAsset className="absolute top-1/2 right-1/4 w-16 md:w-20">
                        <ABCSVGs />
                    </DecorativeAsset>
                </>
            )}
        </div>
    );
};


// Enhanced Learner's Portfolio Page
const LearnerPortfolioPage = () => {
    return (
        <div className="relative min-h-[85vh] bg-sky-200 overflow-hidden font-[poppins] rounded-xl">
            {/* Background patterns */}
            <div className="absolute inset-0 z-0">
                {/* Diagonal patterns */}
                <div className="absolute top-0 left-0 w-1/3 h-1/2">
                    <div className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 -translate-x-1/4"></div>
                </div>
                <div className="absolute bottom-0 right-0 w-1/2 h-1/2">
                    <div className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 translate-x-1/4"></div>
                </div>

                {/* Decorative circles */}
                {[...Array(6)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute rounded-full bg-white opacity-40"
                        style={{
                            width: `${Math.random() * 20 + 10}px`,
                            height: `${Math.random() * 20 + 10}px`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>

            {/* Circle decorations */}
            <div className="absolute top-16 left-16 w-24 h-24 bg-amber-400 rounded-full opacity-80"></div>
            <div
                className="absolute top-0 right-0 w-36 h-36 bg-purple-500 rounded-full opacity-60 transform translate-x-1/3 -translate-y-1/3"></div>

            <div className="relative z-10 p-6 md:p-8">
                {/* Title and note section */}
                <div className="mb-6">
                    <h2 className="text-4xl font-bold text-blue-800 text-center mb-3">Learner's portfolio</h2>
                    <p className="text-blue-700 max-w-3xl mx-auto">
                        NOTE: Paste pictures/ display selected work done by student in various experiential and
                        inter-disciplinary tasks done in class.
                    </p>
                </div>

                {/* Portfolio content area */}
                <div className="relative mx-auto max-w-4xl mb-8">
                    <div className="border-4 border-white rounded-2xl overflow-hidden shadow-lg">
                        <div className="bg-amber-50 p-8 min-h-[350px] flex items-center justify-center">
                            {/* Upload placeholder */}
                            <div className="text-center text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-24 h-24 mx-auto mb-4 opacity-30"
                                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <div className="space-y-2">
                                    <p className="text-lg font-medium">Upload student work samples here</p>
                                    <p className="text-sm">Click to browse files or drag and drop</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Signature section */}
                <div className="mb-6">
                    <h3 className="text-3xl font-bold text-blue-800 text-center mb-4">Signature with date</h3>

                    <div className="max-w-4xl mx-auto rounded-xl overflow-hidden border-4 border-white">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr>
                                    <th className="bg-red-200 p-3 text-blue-800 font-bold">Term</th>
                                    <th className="bg-red-200 p-3 text-blue-800 font-bold">Parent/Guardian</th>
                                    <th className="bg-red-200 p-3 text-blue-800 font-bold">Class Teacher</th>
                                    <th className="bg-red-200 p-3 text-blue-800 font-bold">Principal</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="p-4 bg-amber-200 text-blue-800 font-medium text-center">Term 1</td>
                                    <td className="p-4 bg-amber-200"></td>
                                    <td className="p-4 bg-amber-200"></td>
                                    <td className="p-4 bg-amber-200"></td>
                                </tr>
                                <tr>
                                    <td className="p-4 bg-amber-200 text-blue-800 font-medium text-center">Term 2</td>
                                    <td className="p-4 bg-amber-200"></td>
                                    <td className="p-4 bg-amber-200"></td>
                                    <td className="p-4 bg-amber-200"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Music note decoration */}
                <div className="absolute bottom-32 right-12 text-red-500">
                    <svg viewBox="0 0 24 24" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18V5l12-2v13"></path>
                        <circle cx="6" cy="18" r="3"></circle>
                        <circle cx="18" cy="16" r="3"></circle>
                    </svg>
                </div>

                {/* Trees decoration in bottom right */}
                <div className="absolute bottom-8 right-8">
                    <svg viewBox="0 0 120 80" className="w-32 h-20">
                        {/* Tree 1 */}
                        <g transform="translate(10, 10)">
                            <rect x="8" y="30" width="4" height="20" fill="#2F855A" />
                            <circle cx="10" cy="15" r="15" fill="#059669" />
                            <circle cx="10" cy="20" r="12" fill="#10B981" />
                        </g>
                        {/* Tree 2 */}
                        <g transform="translate(40, 5)">
                            <rect x="8" y="30" width="4" height="25" fill="#2F855A" />
                            <circle cx="10" cy="15" r="15" fill="#059669" />
                            <circle cx="10" cy="20" r="12" fill="#10B981" />
                        </g>
                        {/* Tree 3 */}
                        <g transform="translate(75, 15)">
                            <rect x="8" y="25" width="4" height="15" fill="#2F855A" />
                            <circle cx="10" cy="12" r="12" fill="#059669" />
                            <circle cx="10" cy="17" r="10" fill="#10B981" />
                        </g>
                    </svg>
                </div>
            </div>

            {/* Page number */}
            <div className="absolute bottom-4 right-8 font-bold text-blue-800 text-2xl">
                10
            </div>
        </div>
    );
};

// Add required styles for animations and transitions
const portfolioStyles = document.createElement('style');
portfolioStyles.textContent = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  .portfolio-container {
    animation: fadeIn 0.5s ease-out;
  }
  
  .camera-pulse {
    animation: float 3s ease-in-out infinite;
  }
  
  @media print {
    .bg-sky-200 { background-color: #bae6fd !important; }
    .bg-red-200 { background-color: #fecaca !important; }
    .bg-amber-200 { background-color: #fde68a !important; }
    .text-blue-800 { color: #1e40af !important; }
  }
`;
document.head.appendChild(portfolioStyles);


// Add print styles to document - Already defined in ReportStyles.js
const addPrintStyles = () => {
    if (document.getElementById('hpc-print')) return;

    const css = `
  /* ————— 1. Page box ————— */
  @page { size: A4 portrait; margin: 0 }

  /* ————— 2. Global reset ————— */
  @media print {
    html,body{
      width:210mm; min-height:297mm;
      margin:0!important; padding:0!important;
      overflow:visible!important;
      -webkit-print-color-adjust:exact;
              print-color-adjust:exact;
    }

    /* 2 a. Hide every UI/control piece */
    header,nav,button,.fixed,.sticky,
    .print-hidden,[class*="sidebar"]{display:none!important;}

    /* ————— 3. Each logical sheet ————— */
    .print-page{
      width: 210mm; height: 297mm; /* a real A4 */
      page-break-after:always;            /* new PDF sheet afterwards */
      break-after:page;                   /* modern alias   */
      margin:0!important; padding:0!important;
      overflow:visible!important;         /* never clip */
      position:relative; box-sizing:border-box;
    }

    /* 3 a. Remove all Tailwind layout limits INSIDE that sheet */
    .print-page [class*="max-w"],
    .print-page .container      {max-width:210mm!important; width:210mm!important;}

    .print-page [class*="mx-"],
    .print-page [class*="ml-"],
    .print-page [class*="mr-"]  {margin-left:0!important; margin-right:0!important;}

    .print-page [class*="px-"],
    .print-page [class*="pl-"],
    .print-page [class*="pr-"]  {padding-left:0!important; padding-right:0!important;}

    /* 3 b. Optional inner safe‑area – uncomment if your printer clips edges  */
    /* .print-page > *{ padding:6mm 10mm 8mm } */

    /* ————— 4. Misc helpers ————— */
    .no-break, tr         {break-inside:avoid;}
    img,svg               {max-width:100%; height:auto; object-fit:contain;}
  }`;

    const style = document.createElement('style');
    style.id = 'hpc-print';
    style.textContent = css;
    document.head.appendChild(style);
};

// Updated NavigationSidebar component with PDF support
const NavigationSidebar = ({
    currentPage,
    goToPage,
    pages,
    toggleSidebar,
    isSidebarOpen,
    handlePrint,
    studentProfile,
    setReportView,
    downloadAsPdf,
    setDownloadAsPdf,
    isPdfGenerating
}) => (
    <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 print:hidden`}
    >
        <div className="h-full flex flex-col overflow-y-auto">
            {/* Header - Student name and close button */}
            <div
                className="p-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50">
                <div>
                    <h2 className="font-bold text-lg text-blue-800">Report Sections</h2>
                    {studentProfile?.name && (
                        <p className="text-sm text-blue-600 truncate">{studentProfile.name}'s Report</p>
                    )}
                </div>
                <button onClick={toggleSidebar} className="md:hidden text-gray-500 hover:text-gray-700">
                    <X size={20} />
                </button>
            </div>

            {/* Navigation Links */}
            <div className="flex-grow py-4 px-2 space-y-1 overflow-y-auto">
                {pages.map((page, index) => (
                    <button
                        key={index}
                        onClick={() => {
                            goToPage(index);
                            if (window.innerWidth < 768) toggleSidebar();
                        }}
                        className={`w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 transition-all duration-200 ${currentPage === index
                                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md'
                                : 'hover:bg-gray-100'
                            }`}
                    >
                        <div className={`${currentPage === index ? 'text-white' : 'text-blue-600'}`}>
                            {page.icon}
                        </div>
                        <span>{page.name}</span>
                        {currentPage === index && (
                            <div className="ml-auto">
                                <ChevronRight size={16} />
                            </div>
                        )}
                    </button>
                ))}
            </div>

            {/* Actions Section - PDF Download and Print Controls */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="space-y-3">
                    {/* Toggle between PDF and Print */}
                    <div className="mb-2 bg-white p-2 rounded-lg border border-gray-200">
                        <div className="text-sm font-semibold mb-2 text-gray-700">Output Format:</div>
                        <div className="flex space-x-2">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    className="form-radio text-blue-600"
                                    checked={downloadAsPdf}
                                    onChange={() => setDownloadAsPdf(true)}
                                />
                                <span className="ml-2 text-sm">PDF</span>
                            </label>
                            {/*<label className="flex items-center">*/}
                            {/*    <input*/}
                            {/*        type="radio"*/}
                            {/*        className="form-radio text-blue-600"*/}
                            {/*        checked={!downloadAsPdf}*/}
                            {/*        onChange={() => setDownloadAsPdf(false)}*/}
                            {/*    />*/}
                            {/*    <span className="ml-2 text-sm">Print</span>*/}
                            {/*</label>*/}
                        </div>
                    </div>

                    {/* PDF Download Button */}
                    <button
                        onClick={handlePrint}
                        disabled={isPdfGenerating}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md disabled:opacity-70"
                    >
                        {isPdfGenerating ? (
                            <>
                                <RefreshCw size={16} className="animate-spin" />
                                <span>Generating PDF...</span>
                            </>
                        ) : (
                            <>
                                <Download size={16} />
                                <span>{downloadAsPdf ? "Download PDF" : "Print Report"}</span>
                            </>
                        )}
                    </button>

                    {/* Regenerate Button */}
                    <button
                        onClick={() => setReportView('setup')}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all shadow-md"
                    >
                        <RefreshCw size={16} />
                        <span>Regenerate Report</span>
                    </button>
                </div>
            </div>

            {/* Keyboard Shortcut Helper */}
            <div className="p-3 bg-gray-100 border-t border-gray-200 text-center">
                <div className="text-xs text-gray-500 flex items-center justify-center space-x-1">
                    <ArrowLeft size={12} />
                    <span>Use keyboard arrows</span>
                    <ArrowRight size={12} />
                </div>
            </div>
        </div>
    </div>
);

// PrintableReport component for normal printing
const PrintableReport = ({ pages }) => (
    <div className="print-only hidden">
        {pages.map((page, index) => (
            <div key={index} className="print-page print-container">
                {page.component}
            </div>
        ))}
    </div>
);

// PrintPreview component
const PrintPreview = ({ onClose, studentProfile, pages }) => (
    <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center print:hidden">
        <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="font-bold text-xl text-blue-800">Print Preview</h2>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                    <X size={20} />
                </button>
            </div>

            <div className="flex-grow overflow-auto p-6">
                <div className="bg-gray-100 p-4 rounded-lg shadow-inner mb-4">
                    <div className="bg-white p-6 rounded shadow-sm">
                        <h1 className="text-2xl font-bold text-blue-800 mb-2">Holistic Progress Card</h1>
                        <p className="text-gray-700">
                            Student: <span
                                className="font-semibold">{studentProfile?.name || 'Student Name'}</span><br />
                            Class: <span
                                className="font-semibold">{studentProfile?.class_grade || 'N/A'} {studentProfile?.section ? `- ${studentProfile?.section}` : ''}</span><br />
                            School: <span className="font-semibold">{studentProfile?.school_name || 'N/A'}</span>
                        </p>

                        <div className="mt-4 py-2 px-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-md">
                            <p className="text-sm text-yellow-800">This document will include all {pages.length} pages
                                of the report.</p>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <h3 className="font-medium text-blue-800 mb-2">Print Options</h3>
                        <div className="space-y-2">
                            <label className="flex items-center space-x-2 text-sm">
                                <input type="checkbox" className="form-checkbox text-blue-600" checked />
                                <span>Include all pages</span>
                            </label>
                            <label className="flex items-center space-x-2 text-sm">
                                <input type="checkbox" className="form-checkbox text-blue-600" checked />
                                <span>Print in color</span>
                            </label>
                            <label className="flex items-center space-x-2 text-sm">
                                <input type="checkbox" className="form-checkbox text-blue-600" checked />
                                <span>Include graphics and illustrations</span>
                            </label>
                        </div>
                    </div>

                    <div className="flex items-center justify-center space-x-4">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => window.print()}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <Printer size={18} />
                            <span>Print Report</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
);


// Professional Report Check State
const ReportCheckView = ({ setReportView }) => (
    <Layout>
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
            <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
                {/* Fixed-size loader container */}
                <div className="h-20 mb-4 flex items-center justify-center">
                    <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
                        <div
                            className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
                    </div>
                </div>

                <h2 className="text-xl font-bold text-blue-800 mb-2">Checking Existing Reports</h2>
                <p className="text-sm text-gray-600 mb-6">Please wait while we verify available report data</p>

                {/* Fixed-height status container */}
                <div
                    className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-6 h-32 flex flex-col justify-center">
                    <div className="text-left">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Looking for:</h3>
                        <ul className="space-y-2 text-sm">
                            <li className="flex items-center">
                                <div className="w-4 h-4 mr-2 rounded-full bg-blue-200"></div>
                                <span>Student Profile</span>
                            </li>
                            <li className="flex items-center">
                                <div className="w-4 h-4 mr-2 rounded-full bg-blue-200"></div>
                                <span>Domain Reports</span>
                            </li>
                            <li className="flex items-center">
                                <div className="w-4 h-4 mr-2 rounded-full bg-blue-200"></div>
                                <span>Assessment Data</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <button
                    onClick={() => setReportView('setup')}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    Skip checking
                </button>
            </div>
        </div>
    </Layout>
);

// Professional Loading State
const LoadingView = () => (
    <Layout>
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
            <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
                {/* Fixed-size loader container */}
                <div className="h-20 mb-4 flex items-center justify-center">
                    <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-full border-4 border-gray-100"></div>
                        <div
                            className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
                    </div>
                </div>

                <h2 className="text-xl font-bold text-blue-800 mb-2">Loading Your Report</h2>
                <p className="text-sm text-gray-600 mb-6">We're retrieving all report components</p>

                {/* Fixed-height progress container */}
                <div className="h-8 mb-6">
                    <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-600 rounded-full" style={{ width: "60%" }}></div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
);

// Professional Error State
const ErrorView = ({ error, setReportView }) => (
    <Layout>
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
            <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
                {/* Fixed-size icon container */}
                <div className="h-20 mb-4 flex items-center justify-center">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
                        <AlertCircle size={32} className="text-red-500" />
                    </div>
                </div>

                <h2 className="text-xl font-bold text-red-800 mb-2">Error Loading Report</h2>
                <p className="text-sm text-gray-600 mb-6 h-16 flex items-center justify-center">{error}</p>

                <button
                    onClick={() => setReportView('setup')}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    Back to Setup
                </button>
            </div>
        </div>
    </Layout>
);

// Professional Report Setup View
const ReportSetupView = ({
    reportsExist,
    domains,
    fetchAllReportData,
    generateAllReports,
    timespan,
    setTimespan,
    days,
    setDays,
    months,
    setMonths
}) => (
    <Layout>
        <div className="bg-gradient-to-b from-blue-50 to-blue-100 min-h-screen py-10">
            <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-xl shadow-lg p-8">
                    {/* Header */}
                    <div className="border-b border-gray-200 pb-4 mb-6">
                        <h1 className="text-2xl font-bold text-blue-800">Generate Student Report</h1>
                        <p className="text-gray-600">Configure and generate comprehensive student reports</p>
                    </div>

                    {/* Report Status Section */}
                    <div className="mb-8 bg-blue-50 p-6 rounded-lg border border-blue-100">
                        <h3 className="text-lg font-medium text-blue-800 mb-4 flex items-center">
                            <Info className="w-5 h-5 mr-2 text-blue-500" />
                            Available Reports
                        </h3>

                        {Object.keys(reportsExist).length > 0 ? (
                            <div className="space-y-2">
                                <div className="flex items-center mb-3">
                                    <div className="h-2.5 w-2.5 rounded-full bg-green-500 mr-2"></div>
                                    <span className="text-sm text-gray-700 mr-4">Available</span>
                                    <div className="h-2.5 w-2.5 rounded-full bg-red-500 mr-2"></div>
                                    <span className="text-sm text-gray-700">Not Found</span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {/* Profile Summary */}
                                    <div
                                        className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded-lg">
                                        <span className="font-medium">Profile Summary</span>
                                        <div
                                            className={`h-3 w-3 rounded-full ${reportsExist.profileSummary ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                    </div>

                                    {/* Domains */}
                                    {domains.map(domain => (
                                        <div key={domain}
                                            className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded-lg">
                                            <span className="font-medium truncate">{domain}</span>
                                            <div
                                                className={`h-3 w-3 rounded-full ${reportsExist[`domain_${domain}`] ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                        </div>
                                    ))}

                                    {/* Other reports */}
                                    <div
                                        className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded-lg">
                                        <span className="font-medium">Parent Feedback</span>
                                        <div
                                            className={`h-3 w-3 rounded-full ${reportsExist.parentFeedback ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                    </div>

                                    <div
                                        className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded-lg">
                                        <span className="font-medium">Self Assessment</span>
                                        <div
                                            className={`h-3 w-3 rounded-full ${reportsExist.selfAssessment ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                    </div>

                                    <div
                                        className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded-lg">
                                        <span className="font-medium">Peer Assessment</span>
                                        <div
                                            className={`h-3 w-3 rounded-full ${reportsExist.peerAssessment ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                    </div>
                                </div>

                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {Object.values(reportsExist).some(val => val === true) && (
                                        <button
                                            onClick={fetchAllReportData}
                                            className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                                        >
                                            <BookOpen className="w-5 h-5" />
                                            <span>View Existing Reports</span>
                                        </button>
                                    )}

                                    <button
                                        onClick={generateAllReports}
                                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        <RefreshCw className="w-5 h-5" />
                                        <span>Generate Missing Reports</span>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="h-24 flex items-center justify-center">
                                <div className="relative w-10 h-10">
                                    <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
                                    <div
                                        className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
                                </div>
                                <p className="ml-3 text-gray-600">Checking for existing reports...</p>
                            </div>
                        )}
                    </div>

                    {/* Report Time Period Configuration */}
                    <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
                        <h3 className="text-lg font-medium text-blue-800 mb-4">Time Period Configuration</h3>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select time span
                                type:</label>
                            <div className="flex space-x-6">
                                <label className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        className="form-radio text-blue-600 h-5 w-5"
                                        name="timespan"
                                        value="days"
                                        checked={timespan === 'days'}
                                        onChange={() => setTimespan('days')}
                                    />
                                    <span className="ml-2 text-gray-700">Days</span>
                                </label>
                                <label className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        className="form-radio text-blue-600 h-5 w-5"
                                        name="timespan"
                                        value="months"
                                        checked={timespan === 'months'}
                                        onChange={() => setTimespan('months')}
                                    />
                                    <span className="ml-2 text-gray-700">Months</span>
                                </label>
                            </div>
                        </div>

                        {/* Fixed height container for inputs */}
                        <div className="h-28">
                            {timespan === 'days' ? (
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Number of days to look back:
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={days}
                                            onChange={(e) => setDays(Math.max(1, parseInt(e.target.value) || 1))}
                                            min="1"
                                            className="w-full p-3 pl-12 border border-gray-300 rounded-md"
                                        />
                                        <div
                                            className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <Calendar className="w-5 h-5 text-gray-400" />
                                        </div>
                                    </div>
                                    <p className="mt-2 text-sm text-gray-500">
                                        Will generate reports based on the last {days} days of data
                                    </p>
                                </div>
                            ) : (
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Number of months to look back:
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={months}
                                            onChange={(e) => setMonths(Math.max(1, parseInt(e.target.value) || 1))}
                                            min="1"
                                            className="w-full p-3 pl-12 border border-gray-300 rounded-md"
                                        />
                                        <div
                                            className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <Calendar className="w-5 h-5 text-gray-400" />
                                        </div>
                                    </div>
                                    <p className="mt-2 text-sm text-gray-500">
                                        Will generate reports based on the last {months} months of data
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={generateAllReports}
                        className="w-full flex items-center justify-center space-x-2 px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        <RefreshCw className="w-5 h-5" />
                        <span>Generate Complete Report</span>
                    </button>

                    <p className="text-center text-sm text-gray-500 mt-4">
                        This process will generate all necessary reports for the student based on submissions,
                        feedback,
                        and assessments in the specified time period.
                    </p>
                </div>
            </div>
        </div>
    </Layout>
);

// Professional Generation Progress View
const GenerationProgressView = ({ generationSteps, allReportsGenerated, fetchAllReportData }) => (
    <Layout>
        <div className="bg-gradient-to-b from-blue-50 to-blue-100 min-h-screen py-10">
            <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-xl shadow-lg p-8">
                    {/* Header */}
                    <div className="border-b border-gray-200 pb-4 mb-6">
                        <h1 className="text-2xl font-bold text-blue-800">Generating Reports</h1>
                        <p className="text-gray-600">Please wait while we prepare your student reports</p>
                    </div>

                    {/* Progress steps - fixed height container */}
                    <div className="mb-6">
                        {generationSteps.map((step) => (
                            <div
                                key={step.id}
                                className="flex items-center border-l-4 pl-4 py-3 mb-2 h-16"
                                style={{
                                    borderLeftColor:
                                        step.status === Status.SUCCESS ? '#10B981' :
                                            step.status === Status.PROCESSING ? '#3B82F6' :
                                                step.status === Status.FAILED ? '#EF4444' :
                                                    '#E5E7EB'
                                }}
                            >
                                <div className="flex-grow">
                                    <p className="font-medium text-gray-800">{step.label}</p>
                                    <p className="text-sm text-gray-500">
                                        {step.status === Status.PENDING && "Waiting to start..."}
                                        {step.status === Status.PROCESSING && "Processing..."}
                                        {step.status === Status.SUCCESS && "Completed successfully"}
                                        {step.status === Status.FAILED && "Process encountered an error"}
                                    </p>
                                </div>
                                <div className="ml-4">
                                    {step.status === Status.PENDING && (
                                        <div
                                            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100">
                                            <Clock className="w-4 h-4 text-gray-400" />
                                        </div>
                                    )}
                                    {step.status === Status.PROCESSING && (
                                        <div
                                            className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100">
                                            <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
                                        </div>
                                    )}
                                    {step.status === Status.SUCCESS && (
                                        <div
                                            className="w-8 h-8 flex items-center justify-center rounded-full bg-green-100">
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        </div>
                                    )}
                                    {step.status === Status.FAILED && (
                                        <div
                                            className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100">
                                            <XCircle className="w-4 h-4 text-red-600" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Overall progress indicator */}
                    <div className="bg-gray-100 h-2 rounded-full overflow-hidden mb-8">
                        <div
                            className="h-full bg-blue-600 rounded-full transition-all duration-500"
                            style={{
                                width: `${(generationSteps.filter(step =>
                                    step.status === Status.SUCCESS ||
                                    step.status === Status.FAILED).length / generationSteps.length) * 100}%`
                            }}
                        ></div>
                    </div>

                    {allReportsGenerated && (
                        <button
                            onClick={() => fetchAllReportData()}
                            className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                            <BookOpen className="w-5 h-5" />
                            <span>View Generated Reports</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    </Layout>
);


// Main Component: FoundationalReport
const FoundationalReport = () => {
    // State management
    const [reportView, setReportView] = useState('setup'); // 'setup', 'generating', 'viewing', or 'check'
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportsExist, setReportsExist] = useState({});
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const [theme, setTheme] = useState('light');

    // PDF download state
    const [downloadAsPdf, setDownloadAsPdf] = useState(true);
    const [isPdfGenerating, setIsPdfGenerating] = useState(false);

    // Refs
    const reportRef = useRef(null);

    // Date range selection
    const [timespan, setTimespan] = useState('months');
    const [days, setDays] = useState(30);
    const [months, setMonths] = useState(3);

    // Generation progress tracking
    const [generationSteps, setGenerationSteps] = useState([
        { id: 'profile', label: 'Profile Summary', status: Status.PENDING },
        { id: 'domains', label: 'Domain Reports', status: Status.PENDING },
        { id: 'parent', label: 'Parent Feedback', status: Status.PENDING },
        { id: 'self', label: 'Self Assessment', status: Status.PENDING },
        { id: 'peer', label: 'Peer Assessment', status: Status.PENDING },
        { id: 'teacher', label: 'Teacher Profile', status: Status.PENDING }
    ]);

    // Data states
    const [profileSummary, setProfileSummary] = useState(null);
    const [studentProfile, setStudentProfile] = useState(null);
    const [domainReports, setDomainReports] = useState({});
    const [parentFeedback, setParentFeedback] = useState(null);
    const [selfAssessment, setSelfAssessment] = useState(null);
    const [peerAssessment, setPeerAssessment] = useState(null);
    const [learnerProfile, setLearnerProfile] = useState(null);

    // Toggle sidebar
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Effect for styles and print setup
    useEffect(() => {
        addPrintStyles();
        addReportStyles();
    }, []);

    // Effect for keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (reportView === 'viewing') {
                if (e.key === 'ArrowRight') {
                    nextPage();
                } else if (e.key === 'ArrowLeft') {
                    prevPage();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentPage, reportView]);

    // Effect for initial report check
    useEffect(() => {
        checkReportsExist();
    }, []);

    // Combined handler for PDF or print
    const handlePrintOrPdf = () => {
        if (downloadAsPdf) {
            setIsPdfGenerating(true);
            setTimeout(() => {
                generatePDF(pages_for_print, studentProfile);
            }, 100);


        } else {
            setShowPrintPreview(true);
        }
    };

    // Helper function for API calls
    const fetchAPI = (() => {
        const pendingRequests = {};

        return async (endpoint, method = 'GET', body = null) => {
            // Create a unique key for this request
            const requestKey = `${method}-${endpoint}-${body ? JSON.stringify(body) : ''}`;

            // If this exact request is already pending, return the existing promise
            if (pendingRequests[requestKey]) {
                return pendingRequests[requestKey];
            }

            try {
                const accessToken = localStorage.getItem("accessToken");
                const options = {
                    method,
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                };

                if (body && (method === 'POST' || method === 'PUT')) {
                    options.body = JSON.stringify(body);
                }

                // Store the promise for this request
                const requestPromise = new Promise(async (resolve, reject) => {
                    try {
                        const response = await fetch(`${API_BASE_URL}${endpoint}`, options);
                        if (!response.ok) {
                            throw new Error(`HTTP error ${response.status}`);
                        }
                        const data = await response.json();
                        resolve(data);
                    } catch (error) {
                        reject(error);
                    } finally {
                        // Clean up this request from pending requests
                        delete pendingRequests[requestKey];
                    }
                });

                pendingRequests[requestKey] = requestPromise;
                return requestPromise;
            } catch (error) {
                console.error(`Error with ${endpoint}:`, error);
                throw error;
            }
        };
    })();

    // Check if reports exist
    const checkReportsExist = async () => {
        if (loading || reportView === 'check') {
            return;
        }

        setReportView('check');
        setLoading(true);

        try {
            const reportStatus = {};

            // Check profile summary
            try {
                await fetchAPI('/student/profile-summary');
                reportStatus.profileSummary = true;
            } catch (err) {
                reportStatus.profileSummary = false;
            }

            // Check domain reports
            for (const domain of domains) {
                try {
                    await fetchAPI('/domain-report/fetch', 'POST', {
                        domain_name: domain
                    });
                    reportStatus[`domain_${domain}`] = true;
                } catch (err) {
                    reportStatus[`domain_${domain}`] = false;
                }
            }

            // Check parent feedback
            try {
                await fetchAPI('/parent-feedback');
                reportStatus.parentFeedback = true;
            } catch (err) {
                reportStatus.parentFeedback = false;
            }

            // Check self assessment
            try {
                await fetchAPI('/self-assessment');
                reportStatus.selfAssessment = true;
            } catch (err) {
                reportStatus.selfAssessment = false;
            }

            // Check peer assessment
            try {
                await fetchAPI('/peer-assessment');
                reportStatus.peerAssessment = true;
            } catch (err) {
                reportStatus.peerAssessment = false;
            }

            // Check teacher profile
            try {
                await fetchAPI('/profile-summary');
                reportStatus.teacherProfile = true;
            } catch (err) {
                reportStatus.teacherProfile = false;
            }

            setReportsExist(reportStatus);

            // Fetch student profile regardless of other reports
            try {
                const profileData = await fetchAPI('/student/profile');
                setStudentProfile(profileData);
            } catch (err) {
                console.error("Could not fetch student profile:", err);
            }

            setLoading(false);
            setReportView('setup');

        } catch (err) {
            console.error("Error checking reports:", err);
            setError("Failed to check existing reports. Please try again.");
            setLoading(false);
            setReportView('setup');
        }
    };

    // Function to generate all reports
    const generateAllReports = async () => {
        setReportView('generating');

        // Update steps function
        const updateStepStatus = (id, status) => {
            setGenerationSteps(steps =>
                steps.map(step => step.id === id ? { ...step, status } : step)
            );
        };

        // Create a params object based on selected timespan
        const timeParams = timespan === 'days'
            ? { days: days }
            : { months: months };

        try {
            // Generate profile summary
            updateStepStatus('profile', Status.PROCESSING);
            try {
                const queryParams = new URLSearchParams(timeParams).toString();
                await fetchAPI(`/student/profile-summary?${queryParams}`, 'POST');
                updateStepStatus('profile', Status.SUCCESS);
            } catch (error) {
                console.error("Profile summary generation failed:", error);
                updateStepStatus('profile', Status.FAILED);
            }

            // Generate domain reports
            updateStepStatus('domains', Status.PROCESSING);
            let domainSuccess = true;
            try {
                for (const domain of domains) {
                    try {
                        await fetchAPI('/create-domain-report', 'POST', {
                            domain_name: domain,
                            ...timeParams
                        });
                    } catch (error) {
                        console.error(`Domain report generation failed for ${domain}:`, error);
                        domainSuccess = false;
                    }
                }
                updateStepStatus('domains', domainSuccess ? Status.SUCCESS : Status.FAILED);
            } catch (error) {
                console.error("Domain reports generation failed:", error);
                updateStepStatus('domains', Status.FAILED);
            }

            // Generate parent feedback
            updateStepStatus('parent', Status.PROCESSING);
            try {
                const queryParams = new URLSearchParams(timeParams).toString();
                await fetchAPI(`/parent-feedback?${queryParams}`, 'POST');
                updateStepStatus('parent', Status.SUCCESS);
            } catch (error) {
                console.error("Parent feedback generation failed:", error);
                updateStepStatus('parent', Status.FAILED);
            }

            // Generate self assessment
            updateStepStatus('self', Status.PROCESSING);
            try {
                const queryParams = new URLSearchParams(timeParams).toString();
                await fetchAPI(`/self-assessment?${queryParams}`, 'POST');
                updateStepStatus('self', Status.SUCCESS);
            } catch (error) {
                console.error("Self assessment generation failed:", error);
                updateStepStatus('self', Status.FAILED);
            }

            // Generate peer assessment
            updateStepStatus('peer', Status.PROCESSING);
            try {
                const queryParams = new URLSearchParams(timeParams).toString();
                await fetchAPI(`/peer-assessment?${queryParams}`, 'POST');
                updateStepStatus('peer', Status.SUCCESS);
            } catch (error) {
                console.error("Peer assessment generation failed:", error);
                updateStepStatus('peer', Status.FAILED);
            }

            // Generate teacher profile
            updateStepStatus('teacher', Status.PROCESSING);
            try {
                const queryParams = new URLSearchParams(timeParams).toString();
                await fetchAPI(`/profile-summary?${queryParams}`, 'POST');
                updateStepStatus('teacher', Status.SUCCESS);
            } catch (error) {
                console.error("Teacher profile generation failed:", error);
                updateStepStatus('teacher', Status.FAILED);
            }

            // Wait for all steps to complete before fetching
            setTimeout(() => {
                const successfulSteps = generationSteps.filter(step =>
                    step.status === Status.SUCCESS).length;

                if (successfulSteps >= 3) {
                    fetchAllReportData();
                }
            }, 2000);

        } catch (error) {
            console.error("Error generating reports:", error);
            setError("Failed to generate reports. Please try again.");
        }
    };

    // Fetch all report data after generation
    const fetchAllReportData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch any missing data we need
            if (!studentProfile) {
                try {
                    const profileData = await fetchAPI('/student/profile');
                    setStudentProfile(profileData);
                } catch (err) {
                    console.error("Could not fetch student profile:", err);
                }
            }

            // Only fetch reports that exist
            if (reportsExist.profileSummary) {
                try {
                    const summaryData = await fetchAPI('/student/profile-summary');
                    setProfileSummary(summaryData);
                } catch (err) {
                    console.error("Could not fetch profile summary:", err);
                }
            }

            // Fetch domain reports - only try to fetch the ones that exist
            const domainData = {};
            for (const domain of domains) {
                const domainKey = `domain_${domain}`;
                if (reportsExist[domainKey]) {
                    try {
                        const data = await fetchAPI('/domain-report/fetch', 'POST', {
                            domain_name: domain
                        });
                        domainData[domain] = data;
                    } catch (err) {
                        console.error(`Error fetching ${domain}:`, err);
                        domainData[domain] = null;
                    }
                } else {
                    domainData[domain] = null;
                }
            }
            setDomainReports(domainData);

            // Only fetch parent feedback if it exists
            if (reportsExist.parentFeedback) {
                try {
                    const parentData = await fetchAPI('/parent-feedback');
                    setParentFeedback(parentData);
                } catch (err) {
                    console.error("Could not fetch parent feedback:", err);
                }
            }

            // Only fetch self assessment if it exists
            if (reportsExist.selfAssessment) {
                try {
                    const selfData = await fetchAPI('/self-assessment');
                    setSelfAssessment(selfData);
                } catch (err) {
                    console.error("Could not fetch self assessment:", err);
                }
            }

            // Only fetch peer assessment if it exists
            if (reportsExist.peerAssessment) {
                try {
                    const peerData = await fetchAPI('/peer-assessment');
                    setPeerAssessment(peerData);
                } catch (err) {
                    console.error("Could not fetch peer assessment:", err);
                }
            }

            // Only fetch learner profile if it exists
            if (reportsExist.teacherProfile) {
                try {
                    const learnerData = await fetchAPI('/profile-summary');
                    setLearnerProfile(learnerData);
                } catch (err) {
                    console.error("Could not fetch teacher profile:", err);
                }
            }

            // Successfully move to viewing screen
            setLoading(false);
            setReportView('viewing');
            setCurrentPage(0);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError("Failed to load report data. Please try again later.");
            setLoading(false);
        }
    };

    // Check if all reports are generated successfully
    const allReportsGenerated = generationSteps.every(
        step => step.status === Status.SUCCESS || step.status === Status.FAILED
    );

    // Show View Reports button when all generations are complete
    useEffect(() => {
        if (reportView === 'generating' && allReportsGenerated) {
            const anyFailed = generationSteps.some(step => step.status === Status.FAILED);
            if (!anyFailed) {
                // If everything succeeded, automatically move to viewing
                fetchAllReportData();
            }
        }
    }, [generationSteps, reportView]);

    // Navigation handlers
    const nextPage = () => {
        if (currentPage < pages.length - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToPage = (pageIndex) => {
        if (pageIndex >= 0 && pageIndex < pages.length) {
            setCurrentPage(pageIndex);
        }
    };


    // 3. A consistent wrapper for the entire application
    const ReportContainer = ({ children }) => (
        <div className="flex justify-center items-center min-h-screen bg-gray-100 p-0 md:p-4">
            {children}
        </div>
    );

    // Define pages with proper 8
    const pages = [
        {
            name: "Cover",
            icon: <FileText className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={1}>
                        <HolisticProgressCardPage />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Student Info",
            icon: <User className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={2}>
                        <CoverPage studentProfile={studentProfile} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "All About Me",
            icon: <User className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={3}>
                        <AllAboutMePage studentProfile={studentProfile} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Profile Summary",
            icon: <BookOpen className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={4}>
                        <ProfileSummaryPage profileSummary={profileSummary} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Physical Dev",
            icon: <Award className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={5}>
                        <DomainReportPage domain="Physical Development" report={domainReports["Physical Development"]} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Socio-Emotional",
            icon: <Heart className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={6}>
                        <DomainReportPage
                            domain="Socio-Emotional and Ethical Development"
                            report={domainReports["Socio-Emotional and Ethical Development"]}
                        />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Cognitive",
            icon: <Brain className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={7}>
                        <DomainReportPage
                            domain="Cognitive Development"
                            report={domainReports["Cognitive Development"]}
                        />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Language",
            icon: <MessageSquare className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={8}>
                        <DomainReportPage
                            domain="Language and Literacy Development"
                            report={domainReports["Language and Literacy Development"]}
                        />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Teacher's Profile",
            icon: <Users className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={9}>
                        <TeacherProfilePage learnerProfile={learnerProfile} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Parent Feedback",
            icon: <Users className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={10}>
                        <ParentFeedbackPage parentFeedback={parentFeedback} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Assessment",
            icon: <CheckCircle className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={11}>
                        <AssessmentPage selfAssessment={selfAssessment} peerAssessment={peerAssessment} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Portfolio",
            icon: <BookOpen className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={12}>
                        <LearnerPortfolioPage />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        }
    ];

    const pages_for_print = [
        {
            name: "Cover",
            icon: <FileText className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={1}>
                        <HolisticProgressCardPage forPdfRender={true} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Student Info",
            icon: <User className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={2}>
                        <CoverPage studentProfile={studentProfile} forPdfRender={true} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "All About Me",
            icon: <User className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={3}>
                        <AllAboutMePage studentProfile={studentProfile} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Profile Summary",
            icon: <BookOpen className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={4}>
                        <ProfileSummaryPage profileSummary={profileSummary} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Physical Dev",
            icon: <Award className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={5}>
                        <DomainReportPage domain="Physical Development" report={domainReports["Physical Development"]} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Socio-Emotional",
            icon: <Heart className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={6}>
                        <DomainReportPage
                            domain="Socio-Emotional and Ethical Development"
                            report={domainReports["Socio-Emotional and Ethical Development"]}
                        />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Cognitive",
            icon: <Brain className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={7}>
                        <DomainReportPage
                            domain="Cognitive Development"
                            report={domainReports["Cognitive Development"]}
                        />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Language",
            icon: <MessageSquare className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={8}>
                        <DomainReportPage
                            domain="Language and Literacy Development"
                            report={domainReports["Language and Literacy Development"]}
                        />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Teacher's Profile",
            icon: <Users className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={9}>
                        <TeacherProfilePage learnerProfile={learnerProfile} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Parent Feedback",
            icon: <Users className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={10}>
                        <ParentFeedbackPage parentFeedback={parentFeedback} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Assessment",
            icon: <CheckCircle className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={11}>
                        <AssessmentPage selfAssessment={selfAssessment} peerAssessment={peerAssessment} />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        },
        {
            name: "Portfolio",
            icon: <BookOpen className="w-5 h-5" />,
            component: (
                <ReportContainer>
                    <ReportPageWrapper pageNumber={12}>
                        <LearnerPortfolioPage />
                    </ReportPageWrapper>
                </ReportContainer>
            )
        }
    ];

    // Initial report check loading state with enhanced animation
    if (reportView === 'check') {
        return <ReportCheckView setReportView={setReportView} />;
    }

    // For loading state
    if (loading) {
        return <LoadingView />;
    }

    // For error state
    if (error) {
        return <ErrorView error={error} setReportView={setReportView} />;
    }

    // For setup view
    if (reportView === 'setup') {
        return (
            <ReportSetupView
                reportsExist={reportsExist}
                domains={domains}
                fetchAllReportData={fetchAllReportData}
                generateAllReports={generateAllReports}
                timespan={timespan}
                setTimespan={setTimespan}
                days={days}
                setDays={setDays}
                months={months}
                setMonths={setMonths}
            />
        );
    }

    // For generation progress view
    if (reportView === 'generating') {
        return (
            <GenerationProgressView
                generationSteps={generationSteps}
                allReportsGenerated={allReportsGenerated}
                fetchAllReportData={fetchAllReportData}
            />
        );
    }

    // Main report view
    return (
        <Layout>
            <div
                className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-b from-blue-50 to-blue-100'}`}>
                {/* Mobile sidebar toggle */}
                <button
                    onClick={toggleSidebar}
                    className="fixed top-4 left-4 z-50 md:hidden bg-white p-2 rounded-md shadow-md text-gray-700 hover:bg-gray-100 print:hidden"
                >
                    <Menu size={20} />
                </button>

                {/* Navigation Sidebar with enhanced props */}
                <NavigationSidebar
                    currentPage={currentPage}
                    goToPage={goToPage}
                    pages={pages}
                    toggleSidebar={toggleSidebar}
                    isSidebarOpen={isSidebarOpen}
                    handlePrint={handlePrintOrPdf}
                    studentProfile={studentProfile}
                    setReportView={setReportView}
                    downloadAsPdf={downloadAsPdf}
                    setDownloadAsPdf={setDownloadAsPdf}
                    isPdfGenerating={isPdfGenerating}
                />

                {/* Main Content Area */}
                <div className={`transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'ml-0'} print:m-0`}>
                    {/* Report Content */}
                    <div
                        ref={reportRef}
                        className="max-w-7xl mx-auto p-3 pb-20 transition-all duration-300 print:p-0 print:m-0"
                    >
                        {/* Printable version with proper pagination */}
                        <PrintableReport pages={pages} />

                        {/* Main report preview with proper A4 sizing */}
                        <div className="mb-6 print:hidden">
                            <PreviewPageWrapper
                                currentPage={currentPage}
                                totalPages={pages.length}
                            >
                                {pages[currentPage].component}
                            </PreviewPageWrapper>
                        </div>

                        {/* Pagination Controls */}
                        <div className="flex justify-between items-center print:hidden">
                            <button
                                onClick={prevPage}
                                disabled={currentPage === 0}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-md shadow-sm ${currentPage === 0
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                                    }`}
                            >
                                <ChevronLeft className="w-5 h-5" />
                                <span>Previous</span>
                            </button>

                            <div
                                className={`${theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-white text-gray-700'} px-4 py-2 rounded-md shadow-sm flex items-center space-x-1`}>
                                <span>Page</span>
                                <select
                                    value={currentPage}
                                    onChange={(e) => goToPage(Number(e.target.value))}
                                    className={`form-select border-0 focus:ring-0 ${theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-white text-gray-700'}`}
                                >
                                    {pages.map((page, index) => (
                                        <option key={index} value={index}>{index + 1}</option>
                                    ))}
                                </select>
                                <span>of {pages.length}</span>
                            </div>

                            <button
                                onClick={nextPage}
                                disabled={currentPage === pages.length - 1}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-md shadow-sm ${currentPage === pages.length - 1
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                                    }`}
                            >
                                <span>Next</span>
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Print Preview Modal - Only shown when explicitly requested */}
                {showPrintPreview && (
                    <PrintPreview
                        onClose={() => setShowPrintPreview(false)}
                        studentProfile={studentProfile}
                        pages={pages}
                    />
                )}
            </div>
        </Layout>
    );
};


const CoverPage = ({ studentProfile, forPdfRender = false }) => {
    if (!studentProfile) return (
        <div className="flex items-center justify-center h-full">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                <RefreshCw className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-3" />
                <p className="text-base text-gray-600">Loading student profile...</p>
            </div>
        </div>
    );

    const { name, class_grade, section, school_name } = studentProfile;
    const initial = name ? name.charAt(0).toUpperCase() : "?";

    // Use simplified version for PDF rendering
    if (forPdfRender) {
        return (
            <div className="relative w-full h-full bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
                {/* Simplified content for PDF */}
                <div className="w-full h-full p-8 flex flex-col items-center justify-center">
                    {/* Student initial avatar - simplified */}
                    <div className="mb-10 relative">
                        <div
                            className="w-48 h-48 mx-auto bg-gradient-to-br from-blue-500 to-indigo-700 rounded-full flex items-center justify-center shadow-xl">
                            <div className="w-44 h-44 bg-white rounded-full flex items-center justify-center">
                                <div
                                    className="w-36 h-36 bg-gradient-to-br from-yellow-400 to-amber-600 rounded-full flex items-center justify-center text-white text-7xl font-extrabold">
                                    {initial}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main title - simplified */}
                    <div className="text-center mb-8">
                        <h1 className="text-5xl font-black mb-2 text-blue-600">
                            Student Progress Report
                        </h1>
                        <div className="flex items-center justify-center">
                            <div className="h-px w-16 bg-blue-300"></div>
                            <h2 className="text-3xl font-bold text-indigo-700 mx-4 tracking-wide">{name || 'Student Name'}</h2>
                            <div className="h-px w-16 bg-blue-300"></div>
                        </div>
                    </div>

                    {/* Info card - simplified */}
                    <div
                        className="bg-white bg-opacity-90 rounded-xl p-6 shadow-lg border border-blue-100 space-y-4 text-xl max-w-xl mx-auto">
                        <div
                            className="absolute -right-3 -top-3 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-lg shadow-lg">
                            Academic Year 2024-25
                        </div>

                        <div className="flex items-center py-3 border-b border-blue-100">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                                <div className="w-5 h-5 text-blue-600">📚</div>
                            </div>
                            <div className="flex flex-col md:flex-row md:items-center flex-1">
                                <span className="font-medium text-gray-700 min-w-[120px]">Class:</span>
                                <span
                                    className="text-blue-800 font-semibold">{class_grade || 'N/A'} {section ? `- ${section}` : ''}</span>
                            </div>
                        </div>

                        <div className="flex items-center py-3 border-b border-blue-100">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                                <div className="w-5 h-5 text-blue-600">🏫</div>
                            </div>
                            <div className="flex flex-col md:flex-row md:items-center flex-1">
                                <span className="font-medium text-gray-700 min-w-[120px]">School:</span>
                                <span className="text-blue-800 font-semibold">{school_name || 'N/A'}</span>
                            </div>
                        </div>

                        <div className="flex items-center py-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                                <div className="w-5 h-5 text-blue-600">📅</div>
                            </div>
                            <div className="flex flex-col md:flex-row md:items-center flex-1">
                                <span className="font-medium text-gray-700 min-w-[120px]">Academic Year:</span>
                                <span className="text-blue-800 font-semibold">2024-2025</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Original interactive version for normal display
    return (
        <div className="relative overflow-hidden min-h-[80vh] flex flex-col items-center justify-center">
            {/* Dynamic animated background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 z-0">
                <div className="absolute inset-0 opacity-10">
                    {Array.from({ length: 20 }).map((_, i) => (
                        <div
                            key={i}
                            className="absolute rounded-full bg-blue-600"
                            style={{
                                width: `${Math.random() * 30 + 10}px`,
                                height: `${Math.random() * 30 + 10}px`,
                                top: `${Math.random() * 100}%`,
                                left: `${Math.random() * 100}%`,
                                opacity: Math.random() * 0.5 + 0.3,
                                animation: `float ${Math.random() * 20 + 15}s infinite ease-in-out`,
                                animationDelay: `${Math.random() * 5}s`,
                                transform: `translateZ(${Math.random() * 50}px)`,
                            }}
                        />
                    ))}
                </div>
            </div>

            {/* Main content with 3D perspective */}
            <div className="relative z-10 w-full max-w-4xl px-6 py-8 perspective-1000">
                {/* Animated top decorative bar */}
                <div
                    className="w-full h-3 bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 rounded-full mb-8 transform hover:scale-x-105 transition-transform duration-500 shadow-lg"></div>

                {/* Student initial avatar with 3D effect */}
                <div className="mb-10 relative group perspective">
                    <div
                        className="absolute inset-0 bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 rounded-full blur opacity-30 group-hover:opacity-40 transition-opacity duration-300 transform -translate-y-2"></div>
                    <div
                        className="transform transition-transform duration-700 group-hover:rotate-y-180 preserve-3d">
                        <div
                            className="w-48 h-48 mx-auto bg-gradient-to-br from-blue-500 to-indigo-700 rounded-full flex items-center justify-center shadow-2xl backface-hidden">
                            <div
                                className="w-44 h-44 bg-white rounded-full flex items-center justify-center backface-hidden">
                                <div
                                    className="w-36 h-36 bg-gradient-to-br from-yellow-400 to-amber-600 rounded-full flex items-center justify-center text-white text-7xl font-extrabold shadow-inner">
                                    {initial}
                                </div>
                            </div>
                        </div>

                        {/* Back side of 3D card effect */}
                        <div
                            className="absolute inset-0 w-48 h-48 mx-auto bg-gradient-to-br from-indigo-600 to-purple-700 rounded-full flex items-center justify-center shadow-2xl backface-hidden rotate-y-180">
                            <div className="text-white text-center p-4">
                                <p className="font-bold">Hello!</p>
                                <p className="text-xs">Click to flip</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main title with animated gradient */}
                <div className="text-center mb-8">
                    <h1 className="text-5xl font-black mb-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent bg-size-200 animate-gradient-x">
                        Student Progress Report
                    </h1>
                    <div className="flex items-center justify-center">
                        <div
                            className="h-px w-16 bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
                        <h2 className="text-3xl font-bold text-indigo-700 mx-4 tracking-wide">{name || 'Student Name'}</h2>
                        <div
                            className="h-px w-16 bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
                    </div>
                </div>

                {/* Info card with glassmorphism effect and interactive elements */}
                <div
                    className="bg-white bg-opacity-20 backdrop-filter backdrop-blur-lg rounded-xl p-6 shadow-xl border border-white border-opacity-30 space-y-4 text-xl max-w-xl mx-auto transform transition-all duration-500 hover:shadow-2xl hover:scale-102 group">
                    <div
                        className="absolute -right-3 -top-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-lg shadow-lg transform rotate-12 group-hover:rotate-0 transition-transform duration-300">
                        Academic Year 2024-25
                    </div>

                    <div
                        className="flex items-center py-3 border-b border-blue-100 group-hover:border-blue-300 transition-colors duration-300">
                        <div
                            className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors duration-300">
                            <BookOpen className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center flex-1">
                            <span className="font-medium text-gray-700 min-w-[120px]">Class:</span>
                            <span
                                className="text-blue-800 font-semibold">{class_grade || 'N/A'} {section ? `- ${section}` : ''}</span>
                        </div>
                    </div>

                    <div
                        className="flex items-center py-3 border-b border-blue-100 group-hover:border-blue-300 transition-colors duration-300">
                        <div
                            className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors duration-300">
                            <Home className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center flex-1">
                            <span className="font-medium text-gray-700 min-w-[120px]">School:</span>
                            <span className="text-blue-800 font-semibold">{school_name || 'N/A'}</span>
                        </div>
                    </div>

                    <div
                        className="flex items-center py-3 group-hover:border-blue-300 transition-colors duration-300">
                        <div
                            className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors duration-300">
                            <Calendar className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center flex-1">
                            <span className="font-medium text-gray-700 min-w-[120px]">Academic Year:</span>
                            <span className="text-blue-800 font-semibold">2024-2025</span>
                        </div>
                    </div>
                </div>

                {/* Interactive button */}
                <div className="text-center mt-10">
                    <button
                        className="group relative inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg shadow-lg overflow-hidden transition-all duration-300 hover:from-indigo-600 hover:to-blue-600">
                        <span className="relative z-10 flex items-center">
                            <ChevronDown
                                className="w-5 h-5 mr-2 transform group-hover:translate-y-1 transition-transform duration-300" />
                            <span>Continue to Report</span>
                        </span>
                        <span
                            className="absolute inset-0 bg-gradient-to-r from-indigo-800 to-blue-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    </button>
                </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute bottom-10 left-10 text-yellow-500 animate-spin-slow">
                <Star className="w-14 h-14 filter drop-shadow-lg" fill="currentColor" />
            </div>
            <div className="absolute top-24 right-24 text-blue-400 animate-float">
                <CloudSnow className="w-10 h-10 filter drop-shadow" />
            </div>
            <div className="absolute bottom-24 right-24 text-amber-500 animate-pulse">
                <Zap className="w-12 h-12 filter drop-shadow-lg" fill="currentColor" />
            </div>
            <div className="absolute top-32 left-24 text-purple-400 animate-bounce delay-200">
                <div className="w-6 h-6 rounded-full bg-current filter drop-shadow-lg"></div>
            </div>
            <div className="absolute bottom-40 right-40 text-green-400 animate-ping delay-300 opacity-60">
                <div className="w-4 h-4 rounded-full bg-current filter drop-shadow-lg"></div>
            </div>
        </div>
    );
};

// Add required styles to head element for 3D effects
if (typeof document !== 'undefined' && !document.getElementById('cover-styles')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'cover-styles';
    styleElement.textContent = `
    .perspective-1000 {
      perspective: 1000px;
    }
    .perspective {
      perspective: 1000px;
    }
    .preserve-3d {
      transform-style: preserve-3d;
    }
    .backface-hidden {
      backface-visibility: hidden;
    }
    .rotate-y-180 {
      transform: rotateY(180deg);
    }
    .scale-102 {
      transform: scale(1.02);
    }
    .bg-size-200 {
      background-size: 200% auto;
    }
    .animate-gradient-x {
      animation: gradient-x 3s linear infinite;
    }
    @keyframes gradient-x {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes float {
      0%, 100% { transform: translateY(0) rotate(0deg); }
      25% { transform: translateY(-10px) rotate(5deg); }
      50% { transform: translateY(0) rotate(0deg); }
      75% { transform: translateY(10px) rotate(-5deg); }
    }
  `;
    document.head.appendChild(styleElement);
}

// Enhanced All About Me Page Component - Optimized for A4 printing
const AllAboutMePage = ({ studentProfile }) => {
    if (!studentProfile) return (
        <div className="flex items-center justify-center h-full">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                <RefreshCw className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-3" />
                <p className="text-base text-gray-600">Loading student profile...</p>
            </div>
        </div>
    );

    const {
        name, things_i_like, live_in, birthday, friends_are,
        favourite_colours, favourite_foods, favourite_games, favourite_animals,
        height_term1, weight_term1, height_term2, weight_term2
    } = studentProfile;

    return (
        <div className="relative w-full h-full bg-sky-200 overflow-hidden">
            {/* Background with subtle patterns */}
            <div className="absolute inset-0">
                {/* Diagonal stripe patterns */}
                <div className="absolute top-0 right-0 w-1/3 h-full overflow-hidden">
                    <div className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 translate-x-1/4"></div>
                </div>
                <div className="absolute bottom-0 left-0 w-1/3 h-full overflow-hidden">
                    <div className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 -translate-x-1/4"></div>
                </div>

                {/* Decorative circles */}
                {[...Array(8)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute rounded-full bg-white opacity-30"
                        style={{
                            width: `${Math.random() * 16 + 8}px`,
                            height: `${Math.random() * 16 + 8}px`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>

            <div className="relative z-10 flex flex-col h-full p-4 md:p-6">
                {/* Page Title with playful design */}
                <h2 className="text-3xl font-bold text-blue-800 text-center mb-4 tracking-wide">
                    All about me
                    <div className="w-32 h-1 bg-blue-800 rounded-full mx-auto mt-1"></div>
                </h2>

                {/* Main content area with cream background and rounded edges */}
                <div className="flex-1 mx-auto w-full max-w-4xl overflow-auto">
                    {/* Main form with cream background */}
                    <div className="bg-amber-50 rounded-2xl p-1 border-4 border-white shadow-lg h-full">
                        <div className="bg-amber-50 rounded-xl p-4 md:p-6 h-full overflow-y-auto">
                            <div className="space-y-4 md:space-y-5">
                                {/* Form fields */}
                                <FormField label="My name is" value={name || ''} />
                                <FormField label="Things I like" value={things_i_like || ''} />
                                <FormField label="I live in" value={live_in || ''} />
                                <FormField
                                    label="My birthday"
                                    value={birthday ? new Date(birthday).toLocaleDateString() : ''}
                                />
                                <FormField label="My friends are" value={friends_are || ''} />

                                {/* Favourites section */}
                                <div className="mt-4">
                                    <label className="font-medium text-blue-800 text-base md:text-lg block mb-2">
                                        My favourite:
                                    </label>

                                    <div className="space-y-3 ml-6">
                                        <FormField label="colours" value={favourite_colours || ''} isSubField />
                                        <FormField label="foods" value={favourite_foods || ''} isSubField />
                                        <FormField label="games" value={favourite_games || ''} isSubField />
                                        <FormField label="animals" value={favourite_animals || ''} isSubField />
                                    </div>
                                </div>

                                {/* Term measurements section */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    {/* Term 1 */}
                                    <div className="bg-amber-200 p-4 rounded-xl relative">
                                        <h3 className="text-center font-bold text-blue-800 mb-3 border-b border-blue-800 pb-1 inline-block">
                                            TERM 1
                                        </h3>

                                        <div className="space-y-3">
                                            <MeasurementField
                                                label="My height is"
                                                value={height_term1 || ''}
                                                unit="hand spans"
                                            />
                                            <MeasurementField
                                                label="My weight is"
                                                value={weight_term1 || ''}
                                                unit="kgs"
                                            />
                                        </div>
                                    </div>

                                    {/* Term 2 */}
                                    <div className="bg-amber-200 p-4 rounded-xl relative">
                                        <h3 className="text-center font-bold text-blue-800 mb-3 border-b border-blue-800 pb-1 inline-block">
                                            TERM 2
                                        </h3>

                                        <div className="space-y-3">
                                            <MeasurementField
                                                label="My height is"
                                                value={height_term2 || ''}
                                                unit="hand spans"
                                            />
                                            <MeasurementField
                                                label="My weight is"
                                                value={weight_term2 || ''}
                                                unit="kgs"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Reusable form field component
const FormField = ({ label, value, isSubField = false }) => {
    const labelClass = isSubField
        ? "inline-block font-medium text-blue-700 text-base w-20 mb-1"
        : "inline-block font-medium text-blue-800 text-base w-28 mb-1";

    return (
        <div className="form-field group">
            <label className={labelClass}>{label}</label>
            <div className="relative">
                <div className="h-px w-full bg-blue-300 absolute bottom-0 left-0"></div>
                <div
                    className="h-0.5 w-0 bg-blue-600 absolute bottom-0 left-0 group-hover:w-full transition-all duration-500 ease-out"></div>
                <input
                    type="text"
                    value={value}
                    readOnly
                    className="w-full bg-transparent border-0 focus:ring-0 text-gray-800 pb-1 font-medium text-base"
                />
            </div>
        </div>
    );
};

// Measurement field component for term boxes
const MeasurementField = ({ label, value, unit }) => {
    return (
        <div className="flex items-center text-sm md:text-base">
            <label className="font-medium text-blue-800 mr-2">{label}</label>
            <span className="inline-block min-w-10 text-center font-medium border-b border-blue-800">
                {value}
            </span>
            <span className="ml-2">{unit}.</span>
        </div>
    );
};


// Add required styles to head element
const aboutMeStyleElement = document.createElement('style');
aboutMeStyleElement.textContent = `
  .form-field:hover .label-float {
    transform: translateY(-8px) scale(0.9);
    color: #1e40af;
  }
  
  @keyframes shimmer {
    0% { background-position: -100% 0; }
    100% { background-position: 200% 0; }
  }
`;
document.head.appendChild(aboutMeStyleElement);


// Enhanced Profile Summary Page Component
const ProfileSummaryPage = ({ profileSummary }) => {
    if (!profileSummary) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                <p className="text-lg text-gray-600">Loading profile summary...</p>
            </div>
        </div>
    );

    const { glimpse_of_myself, glimpse_of_my_family } = profileSummary;

    return (
        <div className="relative min-h-[85vh] overflow-hidden bg-sky-200">
            {/* Decorative diagonal patterns */}
            <div className="absolute inset-0 z-0">
                {/* Top right diagonal pattern */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 overflow-hidden">
                    <div
                        className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 translate-x-1/4 -translate-y-1/4"></div>
                </div>

                {/* Bottom left diagonal pattern */}
                <div className="absolute bottom-0 left-0 w-1/2 h-1/2 overflow-hidden">
                    <div
                        className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 -translate-x-1/4 translate-y-1/4"></div>
                </div>

                {/* Bottom right diagonal pattern */}
                <div className="absolute bottom-0 right-0 w-1/3 h-1/3 overflow-hidden">
                    <div
                        className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 translate-x-1/4 translate-y-1/4"></div>
                </div>

                {/* Decorative circles */}
                {[...Array(6)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute rounded-full bg-white opacity-40"
                        style={{
                            width: `${Math.random() * 20 + 10}px`,
                            height: `${Math.random() * 20 + 10}px`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>

            <div className="relative z-10 px-6 py-8">
                {/* First section - Glimpse of myself */}
                <div className="mb-12 relative">
                    {/* Section Title with decorative elements */}
                    <div className="flex items-center mb-4">
                        <div className="w-8 h-8 rounded-full bg-amber-300 border-2 border-white"></div>
                        <div className="h-0.5 flex-grow bg-white mx-2"></div>
                        <h2 className="text-3xl font-bold text-blue-800">A glimpse of myself</h2>
                        <div className="h-0.5 flex-grow bg-white mx-2"></div>
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                    </div>

                    {/* Content box */}
                    <div className="relative">
                        <div
                            className="absolute inset-0 bg-amber-50 rounded-3xl border-4 border-white shadow-lg"></div>
                        <div className="relative p-8 min-h-[280px] rounded-3xl">
                            <div className="h-full flex items-center justify-center">
                                {glimpse_of_myself ? (
                                    <p className="text-gray-700 leading-relaxed">{glimpse_of_myself}</p>
                                ) : (
                                    <div className="text-center text-gray-400 italic">
                                        <Camera className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                        <p>This space is for sharing about yourself</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Basketball decoration */}
                        <div
                            className="absolute -top-4 -right-4 w-24 h-24 transform hover:rotate-12 transition-transform duration-500">
                            <BasketballSVG />
                        </div>
                    </div>
                </div>

                {/* Second section - Glimpse of my family */}
                <div className="mb-8 relative mt-16">
                    {/* Section Title with decorative elements */}
                    <div className="flex items-center mb-4">
                        <div
                            className="w-8 h-8 rounded-full bg-amber-300 border-2 border-white hidden md:block"></div>
                        <div className="h-0.5 flex-grow bg-white mx-2"></div>
                        <h2 className="text-3xl font-bold text-blue-800">A glimpse of my family</h2>
                        <div className="h-0.5 flex-grow bg-white mx-2"></div>
                        <div className="w-2 h-2 rounded-full bg-white hidden md:block"></div>
                    </div>

                    {/* Content box */}
                    <div className="relative">
                        <div
                            className="absolute inset-0 bg-amber-50 rounded-3xl border-4 border-white shadow-lg"></div>
                        <div className="relative p-8 min-h-[280px] rounded-3xl">
                            <div className="h-full flex items-center justify-center">
                                {glimpse_of_my_family ? (
                                    <p className="text-gray-700 leading-relaxed">{glimpse_of_my_family}</p>
                                ) : (
                                    <div className="text-center text-gray-400 italic">
                                        <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                        <p>This space is for sharing about your family</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* House decoration */}
                        <div
                            className="absolute -bottom-6 -left-6 w-24 h-24 transform hover:scale-110 transition-transform duration-500">
                            <div className="relative">
                                {/* House with red roof */}
                                <div className="absolute bottom-0 left-0 w-20 h-16 bg-gray-200 rounded-sm"></div>
                                <div
                                    className="absolute bottom-16 left-0 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[14px] border-b-red-500"
                                    style={{ transform: 'scale(2)' }}></div>

                                {/* Window */}
                                <div
                                    className="absolute bottom-6 left-4 w-6 h-6 bg-blue-400 border border-blue-600 grid grid-cols-2 grid-rows-2 gap-0.5">
                                    <div className="bg-white opacity-80"></div>
                                    <div className="bg-white opacity-80"></div>
                                    <div className="bg-white opacity-80"></div>
                                    <div className="bg-white opacity-80"></div>
                                </div>

                                {/* Door */}
                                <div className="absolute bottom-0 right-4 w-4 h-8 bg-red-500 rounded-t-sm"></div>

                                {/* Fence */}
                                <div className="absolute -bottom-2 left-0 right-0 h-4 flex">
                                    {[...Array(8)].map((_, i) => (
                                        <div key={i} className="w-3 h-6 bg-amber-700 rounded-t-sm mx-0.5"></div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Musical note decoration */}
                        <div
                            className="absolute -bottom-6 -right-6 w-12 h-12 text-red-500 transform hover:scale-110 hover:rotate-12 transition-all duration-500">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 18V5l12-2v13"></path>
                                <circle cx="6" cy="18" r="3"></circle>
                                <circle cx="18" cy="16" r="3"></circle>
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Note text */}
                <div className="mt-10 text-sm text-blue-800 font-medium px-6">
                    <p><span className="font-bold">Note:</span> Paste a photo or draw a picture of you and your
                        family
                        in the given space above.</p>
                </div>
            </div>

            {/* Large circle decoration in bottom right */}
            <div
                className="absolute bottom-4 right-8 w-16 h-16 border-4 border-sky-300 rounded-full bg-sky-200 flex items-center justify-center z-10">
                <div className="font-bold text-blue-800 text-3xl">3</div>
            </div>
        </div>
    );
};

// Add required styles to head element
const profileStyleElement = document.createElement('style');
profileStyleElement.textContent = `
  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    25% { transform: translateY(-10px) rotate(5deg); }
    50% { transform: translateY(0) rotate(0deg); }
    75% { transform: translateY(10px) rotate(-5deg); }
  }
  
  @keyframes pulse-border {
    0%, 100% { border-color: rgba(255, 255, 255, 0.8); }
    50% { border-color: rgba(255, 255, 255, 0.5); }
  }
`;
document.head.appendChild(profileStyleElement);


// Enhanced Domain Report Page Component - Optimized for A4 printing with API integration
const DomainReportPage = ({ domain, report, pageNumber }) => {
    // Keep hooks at the top level
    const [currentPage, setCurrentPage] = React.useState(1);

    if (!report)
        return (
            <div className="flex items-center justify-center h-full">
                <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                    <RefreshCw className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-3" />
                    <p className="text-base text-gray-600">Loading {domain} report…</p>
                </div>
            </div>
        );

    // Determine if pagination is needed
    const itemsPerPage = 6; // Reduced for better display (can show ~6 competencies per page)
    const totalCompetencies = report.goals.reduce(
        (acc, goal) => acc + goal.competencies.length, 0
    );
    const needsPagination = totalCompetencies > itemsPerPage;

    // Pagination logic - ensures full goals are displayed without truncation
    const paginateContent = (goals) => {
        if (!needsPagination) return { paginatedGoals: goals, totalPages: 1 };

        let competencyCount = 0;
        let pageStart = 0;
        let pageBreaks = [];

        // Find natural break points between goals
        goals.forEach((goal, index) => {
            competencyCount += goal.competencies.length;
            if (competencyCount >= itemsPerPage && index > pageStart) {
                pageBreaks.push(index);
                pageStart = index;
                competencyCount = goal.competencies.length;
            }
        });

        // Add final page break if needed
        if (pageStart < goals.length - 1 && !pageBreaks.includes(goals.length)) {
            pageBreaks.push(goals.length);
        }

        // Calculate total pages
        const totalPages = pageBreaks.length || 1;

        // Get current page content
        const startIdx = currentPage === 1 ? 0 : pageBreaks[currentPage - 2];
        const endIdx = pageBreaks[currentPage - 1] || goals.length;

        return {
            paginatedGoals: goals.slice(startIdx, endIdx),
            totalPages
        };
    };

    const { paginatedGoals, totalPages } = paginateContent(report.goals);

    return (
        <div className="relative w-full h-full bg-sky-200 overflow-hidden">
            <div className="absolute inset-0 flex flex-col py-3 px-4">
                {/* Main content container */}
                <div className="flex-1 flex overflow-hidden rounded-xl shadow-lg bg-white mb-2">
                    {/* ————— Sticky Left Sidebar ————— */}
                    <aside
                        className="flex-none w-28 md:w-32 bg-blue-800 text-white p-3 flex flex-col items-center justify-center space-y-4">
                        <DomainIcon domain={domain} />
                        <h3 className="text-center text-xs md:text-sm font-semibold leading-tight">
                            {domain}
                        </h3>
                    </aside>

                    {/* ————— Table Container ————— */}
                    <div className="flex-1 overflow-auto p-1 md:p-2">
                        <table className="w-full border-collapse">
                            <thead className="sticky top-0 z-10">
                                <tr>
                                    <th className="bg-red-200 p-1 md:p-2 text-center text-blue-800 font-bold border-b border-white text-xs md:text-sm">
                                        Competencies
                                    </th>
                                    <th className="bg-red-200 p-1 md:p-2 text-center text-blue-800 font-bold border-b border-white w-16 md:w-20 text-xs md:text-sm">
                                        Term 1
                                    </th>
                                    <th className="bg-red-200 p-1 md:p-2 text-center text-blue-800 font-bold border-b border-white w-16 md:w-20 text-xs md:text-sm">
                                        Term 2
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedGoals.map((goal, gi) => (
                                    <React.Fragment key={gi}>
                                        <tr>
                                            <td
                                                colSpan={3}
                                                className="bg-amber-200 p-1.5 md:p-2 font-bold text-blue-800 text-xs md:text-sm"
                                            >
                                                Curriculum Goal CG-{goal.id} – {goal.text}
                                            </td>
                                        </tr>

                                        {goal.competencies.map((comp, ci) => (
                                            <tr key={ci}>
                                                <td className="p-1.5 md:p-2 bg-amber-100 text-blue-800 text-xs">
                                                    <span className="font-medium text-blue-700 mr-1">
                                                        C-CG-{goal.id}.{ci + 1}:
                                                    </span>
                                                    {comp.text}
                                                </td>
                                                <td className="p-1.5 md:p-2 bg-amber-50 text-center">
                                                    <ProgressIndicator value={comp.term1 || null} />
                                                </td>
                                                <td className="p-1.5 md:p-2 bg-amber-50 text-center">
                                                    <ProgressIndicator value={comp.term2 || null} />
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination controls if needed */}
                {needsPagination && totalPages > 1 && (
                    <div className="flex justify-center space-x-2 mb-1">
                        {Array.from({ length: totalPages }).map((_, index) => (
                            <button
                                key={index}
                                className={`w-6 h-6 rounded-full text-xs flex items-center justify-center 
                  ${currentPage === index + 1
                                        ? 'bg-blue-800 text-white'
                                        : 'bg-white text-blue-800 border border-blue-800'}`}
                                onClick={() => setCurrentPage(index + 1)}
                            >
                                {index + 1}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* ————— Page Number Badge ————— */}
            <div className="absolute bottom-3 right-4 text-gray-600 font-medium">
                {pageNumber || 5} / 12
            </div>
        </div>
    );
};

// Domain icon component - for visual representation of domains
const DomainIcon = ({ domain }) => {
    switch (domain) {
        case "Physical Development":
            return (
                <svg viewBox="0 0 100 100" className="w-10 h-10 text-orange-500">
                    <circle cx="50" cy="50" r="40" fill="currentColor" />
                    <path d="M50 10 L50 90" stroke="white" strokeWidth="3" />
                    <path d="M10 50 L90 50" stroke="white" strokeWidth="3" />
                    <path d="M20 20 Q50 30 80 20" stroke="white" strokeWidth="2" fill="none" />
                    <path d="M20 80 Q50 70 80 80" stroke="white" strokeWidth="2" fill="none" />
                </svg>
            );
        case "Socio-Emotional and Ethical Development":
            return (
                <svg viewBox="0 0 100 100" className="w-10 h-10 text-pink-500">
                    <path d="M50 20 Q65 10 80 20 Q95 30 90 50 Q85 70 50 90 Q15 70 10 50 Q5 30 20 20 Q35 10 50 20"
                        fill="currentColor" />
                </svg>
            );
        case "Cognitive Development":
            return (
                <svg viewBox="0 0 100 100" className="w-10 h-10 text-purple-500">
                    <ellipse cx="50" cy="35" rx="30" ry="25" fill="currentColor" />
                    <path d="M30 35 Q50 70 70 35" fill="currentColor" />
                </svg>
            );
        case "Language and Literacy Development":
            return (
                <svg viewBox="0 0 100 100" className="w-10 h-10 text-blue-500">
                    <rect x="20" y="20" width="60" height="60" rx="5" fill="currentColor" />
                    <path d="M30 35 L70 35" stroke="white" strokeWidth="3" />
                    <path d="M30 50 L70 50" stroke="white" strokeWidth="3" />
                    <path d="M30 65 L50 65" stroke="white" strokeWidth="3" />
                </svg>
            );
        default:
            return (
                <svg viewBox="0 0 100 100" className="w-10 h-10 text-gray-500">
                    <circle cx="50" cy="50" r="40" fill="currentColor" />
                </svg>
            );
    }
};

// Progress indicator component that handles null/undefined values
const ProgressIndicator = ({ value }) => {
    // Always display a circle, but change its appearance based on value
    let bgColor = 'bg-gray-200'; // Default for null/undefined/empty values
    let tooltip = 'Not assessed';

    if (value) {
        // Only process if value exists (from API)
        switch (value.toLowerCase()) {
            case 'excellent':
            case 'advanced':
                bgColor = 'bg-green-500';
                tooltip = value;
                break;
            case 'good':
            case 'proficient':
                bgColor = 'bg-blue-500';
                tooltip = value;
                break;
            case 'satisfactory':
            case 'developing':
                bgColor = 'bg-yellow-500';
                tooltip = value;
                break;
            case 'needs improvement':
            case 'beginning':
                bgColor = 'bg-orange-500';
                tooltip = value;
                break;
            default:
                // For any other string values that might come from API
                bgColor = 'bg-gray-400';
                tooltip = value;
        }
    }

    return (
        <div
            className={`w-4 h-4 md:w-5 md:h-5 rounded-full ${bgColor} mx-auto`}
            title={tooltip}
        ></div>
    );
};


// Add required styles for animations
const domainStyleElement = document.createElement('style');
domainStyleElement.textContent = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .table-container {
    animation: fadeInUp 0.5s ease-out;
  }
  
  @media print {
    .bg-sky-200 { background-color: #bae6fd !important; }
    .bg-red-200 { background-color: #fecaca !important; }
    .bg-amber-200 { background-color: #fde68a !important; }
    .bg-amber-100 { background-color: #fef3c7 !important; }
    .bg-amber-50 { background-color: #fffbeb !important; }
    .text-blue-800 { color: #1e40af !important; }
  }
`;
document.head.appendChild(domainStyleElement);


// Enhanced Teacher Profile and Parent Feedback Components

// Enhanced Teacher Profile Page Component
const TeacherProfilePage = ({ learnerProfile }) => {
    if (!learnerProfile) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                <p className="text-lg text-gray-600">Loading teacher profile...</p>
            </div>
        </div>
    );

    const { summary } = learnerProfile;

    return (
        <div className="relative min-h-[85vh] font-[poppins] bg-sky-200 overflow-hidden rounded-xl">
            {/* Background patterns */}
            <div className="absolute inset-0 z-0">
                {/* Diagonal patterns */}
                <div className="absolute top-0 right-0 w-1/3 h-2/3">
                    <div className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 translate-x-1/4"></div>
                </div>
                <div className="absolute bottom-0 left-0 w-1/3 h-2/3">
                    <div
                        className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 -translate-x-1/4"></div>
                </div>

                {/* Decorative circles */}
                {[...Array(6)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute rounded-full bg-white opacity-40"
                        style={{
                            width: `${Math.random() * 20 + 10}px`,
                            height: `${Math.random() * 20 + 10}px`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>

            {/* Main content area */}
            <div className="relative z-10 p-6 md:p-8">
                {/* Orange circle decoration */}
                <div className="absolute top-12 left-8 w-20 h-20 bg-amber-400 rounded-full opacity-80"></div>

                {/* Purple semi-circle decoration */}
                <div
                    className="absolute top-6 right-0 w-32 h-32 bg-purple-500 rounded-full opacity-60 transform translate-x-16"></div>

                {/* Title and description - centered on page */}
                <div className="text-center mb-8 relative z-10">
                    <h2 className="text-4xl font-bold text-blue-800 mb-3">Learner's profile by the teacher</h2>
                    <p className="text-blue-700 max-w-3xl mx-auto">
                        Teacher must present a narrative summary of the child, highlighting the strengths,
                        challenges
                        and suggestions for improvement.
                    </p>
                </div>

                {/* Main content box - with cream background and white border */}
                <div className="relative mx-auto max-w-4xl mb-6">
                    <div className="absolute inset-0 border-4 border-white rounded-2xl"></div>
                    <div className="relative bg-amber-50 p-8 rounded-2xl min-h-[350px]">
                        {summary ? (
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{summary}</p>
                        ) : (
                            <div className="flex items-center justify-center h-full text-center">
                                <div className="text-gray-400 italic">
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="w-12 h-12 mx-auto mb-2 opacity-30"
                                    >
                                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                    </svg>
                                    <p>Teacher's summary will appear here</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Music note decoration */}
            <div className="absolute bottom-12 right-12 text-red-500">
                <svg viewBox="0 0 24 24" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18V5l12-2v13"></path>
                    <circle cx="6" cy="18" r="3"></circle>
                    <circle cx="18" cy="16" r="3"></circle>
                </svg>
            </div>

            {/* Page number */}
            <div className="absolute bottom-4 right-8 font-bold text-blue-800 text-2xl">
                8
            </div>
        </div>
    );
};

// Enhanced Parent Feedback Page Component
const ParentFeedbackPage = ({ parentFeedback }) => {
    if (!parentFeedback) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                <p className="text-lg text-gray-600">Loading parent feedback...</p>
            </div>
        </div>
    );

    const { enjoys_participation, supported_for, additional_shares, vaccination } = parentFeedback;

    return (
        <div className="relative min-h-[85vh] font-[poppins] bg-sky-200 overflow-hidden rounded-xl">
            {/* Background patterns */}
            <div className="absolute inset-0 z-0">
                {/* Diagonal patterns */}
                <div className="absolute top-0 right-0 w-1/3 h-2/3">
                    <div className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 translate-x-1/4"></div>
                </div>
                <div className="absolute bottom-0 left-0 w-1/3 h-2/3">
                    <div
                        className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 -translate-x-1/4"></div>
                </div>

                {/* Decorative circles */}
                {[...Array(6)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute rounded-full bg-white opacity-40"
                        style={{
                            width: `${Math.random() * 20 + 10}px`,
                            height: `${Math.random() * 20 + 10}px`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>

            {/* Main content area */}
            <div className="relative z-10 p-6 md:p-8">
                {/* Blue circle decoration */}
                <div className="absolute top-48 left-8 w-24 h-24 bg-blue-400 rounded-full opacity-50"></div>

                {/* Light blue circle decoration */}
                <div className="absolute bottom-12 left-12 w-16 h-16 bg-blue-300 rounded-full opacity-70"></div>

                {/* Smaller circles */}
                <div className="absolute top-24 right-32 w-6 h-6 bg-white rounded-full opacity-80"></div>
                <div className="absolute bottom-32 right-48 w-4 h-4 bg-white rounded-full opacity-80"></div>
                <div className="absolute top-48 left-32 w-3 h-3 bg-white rounded-full opacity-80"></div>

                {/* Title - centered on page */}
                <div className="text-center mb-16 relative z-10 mt-10">
                    <h2 className="text-4xl font-bold text-blue-800 mb-3">Parent's feedback</h2>
                </div>

                {/* Parent Feedback Table */}
                <div className="relative max-w-4xl mx-auto rounded-2xl overflow-hidden border-4 border-white">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr>
                                <th className="bg-red-200 p-4 text-left text-blue-800 font-bold">Aspect</th>
                                <th className="bg-red-200 p-4 text-center text-blue-800 font-bold">Term 1</th>
                                <th className="bg-red-200 p-4 text-center text-blue-800 font-bold">Term 2</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="p-4 bg-amber-200 text-blue-800 font-medium">My child enjoys participating
                                    in...
                                </td>
                                <td className="p-4 bg-amber-100 text-gray-700">{enjoys_participation?.term1 || '-'}</td>
                                <td className="p-4 bg-amber-100 text-gray-700">{enjoys_participation?.term2 || '-'}</td>
                            </tr>
                            <tr>
                                <td className="p-4 bg-amber-200 text-blue-800 font-medium">My child can be supported
                                    for...
                                </td>
                                <td className="p-4 bg-amber-100 text-gray-700">{supported_for?.term1 || '-'}</td>
                                <td className="p-4 bg-amber-100 text-gray-700">{supported_for?.term2 || '-'}</td>
                            </tr>
                            <tr>
                                <td className="p-4 bg-amber-200 text-blue-800 font-medium">I would also like to share
                                    ...
                                </td>
                                <td className="p-4 bg-amber-100 text-gray-700">{additional_shares?.term1 || '-'}</td>
                                <td className="p-4 bg-amber-100 text-gray-700">{additional_shares?.term2 || '-'}</td>
                            </tr>
                            <tr>
                                <td className="p-4 bg-amber-200 text-blue-800 font-medium">
                                    Have I completed age appropriate vaccination schedule for my child?
                                </td>
                                <td className="p-4 bg-amber-100 text-gray-700">{vaccination?.term1 || '-'}</td>
                                <td className="p-4 bg-amber-100 text-gray-700">{vaccination?.term2 || '-'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Colored pencils decoration */}
                <div className="absolute bottom-8 right-8">
                    <svg viewBox="0 0 240 80" className="w-48 h-16">
                        {/* Red pencil */}
                        <g transform="translate(10, 10) rotate(-15)">
                            <rect x="0" y="0" width="40" height="5" fill="#EF4444" />
                            <polygon points="45,2.5 40,5 40,0" fill="#EF4444" />
                            <rect x="0" y="0" width="5" height="5" fill="#000000" />
                        </g>
                        {/* Blue pencil */}
                        <g transform="translate(50, 20) rotate(-5)">
                            <rect x="0" y="0" width="40" height="5" fill="#3B82F6" />
                            <polygon points="45,2.5 40,5 40,0" fill="#3B82F6" />
                            <rect x="0" y="0" width="5" height="5" fill="#000000" />
                        </g>
                        {/* Green pencil */}
                        <g transform="translate(90, 5) rotate(10)">
                            <rect x="0" y="0" width="40" height="5" fill="#10B981" />
                            <polygon points="45,2.5 40,5 40,0" fill="#10B981" />
                            <rect x="0" y="0" width="5" height="5" fill="#000000" />
                        </g>
                        {/* Yellow pencil */}
                        <g transform="translate(130, 15) rotate(-10)">
                            <rect x="0" y="0" width="40" height="5" fill="#F59E0B" />
                            <polygon points="45,2.5 40,5 40,0" fill="#F59E0B" />
                            <rect x="0" y="0" width="5" height="5" fill="#000000" />
                        </g>
                        {/* Purple pencil */}
                        <g transform="translate(170, 25) rotate(-20)">
                            <rect x="0" y="0" width="40" height="5" fill="#8B5CF6" />
                            <polygon points="45,2.5 40,5 40,0" fill="#8B5CF6" />
                            <rect x="0" y="0" width="5" height="5" fill="#000000" />
                        </g>
                    </svg>
                </div>
            </div>

            {/* Page number */}
            <div className="absolute bottom-4 right-8 font-bold text-blue-800 text-2xl">
                8
            </div>
        </div>
    );
};

// Add required styles for animations
const profileFeedbackStyles = document.createElement('style');
profileFeedbackStyles.textContent = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @media print {
    .bg-sky-200 { background-color: #bae6fd !important; }
    .bg-red-200 { background-color: #fecaca !important; }
    .bg-amber-200 { background-color: #fde68a !important; }
    .bg-amber-100 { background-color: #fef3c7 !important; }
    .text-blue-800 { color: #1e40af !important; }
  }
`;
document.head.appendChild(profileFeedbackStyles);


// Self and Peer Assessment Page Component
// Enhanced Assessment Page Component
const AssessmentPage = ({ selfAssessment, peerAssessment }) => {
    // Loading state
    if (!selfAssessment && !peerAssessment) {
        return (
            <div className="flex items-center justify-center min-h-[70vh]">
                <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                    <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-lg text-gray-600">Loading assessment data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative min-h-[85vh] bg-sky-200 overflow-hidden font-[poppins] rounded-xl">
            {/* Background patterns */}
            <div className="absolute inset-0 z-0">
                {/* Diagonal patterns */}
                <div className="absolute top-0 left-0 w-1/3 h-1/3">
                    <div
                        className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 -translate-x-1/4"></div>
                </div>
                <div className="absolute bottom-0 right-0 w-1/2 h-1/2">
                    <div className="w-full h-full bg-blue-300 opacity-20 transform rotate-45 translate-x-1/4"></div>
                </div>

                {/* Decorative circles */}
                {[...Array(6)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute rounded-full bg-white opacity-40"
                        style={{
                            width: `${Math.random() * 20 + 10}px`,
                            height: `${Math.random() * 20 + 10}px`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>

            {/* Circle decorations */}
            <div className="absolute top-16 left-16 w-24 h-24 bg-amber-400 rounded-full opacity-70"></div>
            <div
                className="absolute top-4 right-4 w-32 h-32 bg-purple-500 rounded-full opacity-50 transform translate-x-1/2 -translate-y-1/4"></div>

            <div className="relative z-10 p-6 md:p-8">
                {/* Self Assessment Section */}
                <div className="mb-12">
                    {/* Section Title */}
                    <div className="text-center mb-4">
                        <h2 className="text-4xl font-bold text-blue-800 mb-2">Self-Assessment</h2>
                        <p className="text-blue-700 max-w-3xl mx-auto text-sm">
                            Self reflection on inter-disciplinary activity done by the child. Example: Clay work,
                            drawing, playing a game, colouring, puppet-making, model making, etc.
                        </p>
                    </div>

                    {/* Self Assessment Table */}
                    <div className="max-w-4xl mx-auto overflow-hidden rounded-xl border-4 border-white">
                        {/* Instruction header */}
                        <div className="bg-red-200 p-3 text-blue-800">
                            <p className="font-medium">The teacher must help the children to fill this sheet:</p>
                            <p className="text-sm">(For young children, teachers may fill based on observation and
                                discussion)</p>
                        </div>

                        {/* Table */}
                        <table className="w-full border-collapse">
                            <thead>
                                <tr>
                                    <th className="p-0 w-1/3"></th>
                                    <th className="p-3 text-center bg-red-200 border-l-2 border-white font-bold text-blue-800">
                                        Term 1
                                    </th>
                                    <th className="p-3 text-center bg-red-200 border-l-2 border-white font-bold text-blue-800">
                                        Term 2
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="p-4 bg-amber-200 font-medium text-blue-800">
                                        <span className="mr-2">1.</span>
                                        Activities that I enjoy the most
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white text-gray-800">
                                        {selfAssessment?.enjoy_most?.term1 || ''}
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white text-gray-800">
                                        {selfAssessment?.enjoy_most?.term2 || ''}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-4 bg-amber-200 font-medium text-blue-800">
                                        <span className="mr-2">2.</span>
                                        Activities that I find difficult to do
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white text-gray-800">
                                        {selfAssessment?.find_difficult?.term1 || ''}
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white text-gray-800">
                                        {selfAssessment?.find_difficult?.term2 || ''}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-4 bg-amber-200 font-medium text-blue-800">
                                        <span className="mr-2">3.</span>
                                        Activities that I enjoy doing with my friends
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white text-gray-800">
                                        {selfAssessment?.enjoy_with_friends?.term1 || ''}
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white text-gray-800">
                                        {selfAssessment?.enjoy_with_friends?.term2 || ''}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Music note decoration */}
                <div className="absolute top-48 right-12 text-red-500">
                    <svg viewBox="0 0 24 24" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18V5l12-2v13"></path>
                        <circle cx="6" cy="18" r="3"></circle>
                        <circle cx="18" cy="16" r="3"></circle>
                    </svg>
                </div>

                {/* Peer Assessment Section */}
                <div className="mb-8">
                    {/* Section Title */}
                    <div className="text-center mb-4">
                        <h2 className="text-4xl font-bold text-blue-800 mb-2">Peer-Assessment</h2>
                    </div>

                    {/* Peer Assessment Table */}
                    <div className="max-w-4xl mx-auto overflow-hidden rounded-xl border-4 border-white">
                        {/* Instruction header */}
                        <div className="bg-red-200 p-3 text-blue-800">
                            <p className="font-medium">Peer feedback from classmate(s)</p>
                            <p className="text-sm">Collaborative game/activity such as colouring together, playing a
                                game, etc. done in pairs/groups</p>
                        </div>

                        {/* Table */}
                        <table className="w-full border-collapse">
                            <thead>
                                <tr>
                                    <th className="p-0 w-1/3"></th>
                                    <th className="p-3 text-center bg-red-200 border-l-2 border-white font-bold text-blue-800">
                                        Term 1
                                    </th>
                                    <th className="p-3 text-center bg-red-200 border-l-2 border-white font-bold text-blue-800">
                                        Term 2
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="p-4 bg-amber-200 font-medium text-blue-800">
                                        <span className="mr-2">1.</span>
                                        Helps in completing task/activity
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white">
                                        <div className="flex justify-center space-x-2">
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.helps_complete?.term1 === "Often" ? 'bg-red-400 ring-2 ring-white' : 'bg-red-400 opacity-60'}`}>
                                                <span className="text-lg">😊</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.helps_complete?.term1 === "Rarely" ? 'bg-blue-400 ring-2 ring-white' : 'bg-blue-400 opacity-60'}`}>
                                                <span className="text-lg">😐</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.helps_complete?.term1 === "Sometimes" ? 'bg-yellow-400 ring-2 ring-white' : 'bg-yellow-400 opacity-60'}`}>
                                                <span className="text-lg">🙂</span>
                                            </span>
                                        </div>
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white">
                                        <div className="flex justify-center space-x-2">
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.helps_complete?.term2 === "Often" ? 'bg-red-400 ring-2 ring-white' : 'bg-red-400 opacity-60'}`}>
                                                <span className="text-lg">😊</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.helps_complete?.term2 === "Rarely" ? 'bg-blue-400 ring-2 ring-white' : 'bg-blue-400 opacity-60'}`}>
                                                <span className="text-lg">😐</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.helps_complete?.term2 === "Sometimes" ? 'bg-yellow-400 ring-2 ring-white' : 'bg-yellow-400 opacity-60'}`}>
                                                <span className="text-lg">🙂</span>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-4 bg-amber-200 font-medium text-blue-800">
                                        <span className="mr-2">2.</span>
                                        Likes to play with others
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white">
                                        <div className="flex justify-center space-x-2">
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.likes_play?.term1 === "Often" ? 'bg-red-400 ring-2 ring-white' : 'bg-red-400 opacity-60'}`}>
                                                <span className="text-lg">😊</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.likes_play?.term1 === "Rarely" ? 'bg-blue-400 ring-2 ring-white' : 'bg-blue-400 opacity-60'}`}>
                                                <span className="text-lg">😐</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.likes_play?.term1 === "Sometimes" ? 'bg-yellow-400 ring-2 ring-white' : 'bg-yellow-400 opacity-60'}`}>
                                                <span className="text-lg">🙂</span>
                                            </span>
                                        </div>
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white">
                                        <div className="flex justify-center space-x-2">
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.likes_play?.term2 === "Often" ? 'bg-red-400 ring-2 ring-white' : 'bg-red-400 opacity-60'}`}>
                                                <span className="text-lg">😊</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.likes_play?.term2 === "Rarely" ? 'bg-blue-400 ring-2 ring-white' : 'bg-blue-400 opacity-60'}`}>
                                                <span className="text-lg">😐</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.likes_play?.term2 === "Sometimes" ? 'bg-yellow-400 ring-2 ring-white' : 'bg-yellow-400 opacity-60'}`}>
                                                <span className="text-lg">🙂</span>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-4 bg-amber-200 font-medium text-blue-800">
                                        <span className="mr-2">3.</span>
                                        Shares stationery (crayons/ glue/chalk) with classmates
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white">
                                        <div className="flex justify-center space-x-2">
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.shares_stationery?.term1 === "Often" ? 'bg-red-400 ring-2 ring-white' : 'bg-red-400 opacity-60'}`}>
                                                <span className="text-lg">😊</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.shares_stationery?.term1 === "Rarely" ? 'bg-blue-400 ring-2 ring-white' : 'bg-blue-400 opacity-60'}`}>
                                                <span className="text-lg">😐</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.shares_stationery?.term1 === "Sometimes" ? 'bg-yellow-400 ring-2 ring-white' : 'bg-yellow-400 opacity-60'}`}>
                                                <span className="text-lg">🙂</span>
                                            </span>
                                        </div>
                                    </td>
                                    <td className="p-4 bg-amber-200 border-l-2 border-white">
                                        <div className="flex justify-center space-x-2">
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.shares_stationery?.term2 === "Often" ? 'bg-red-400 ring-2 ring-white' : 'bg-red-400 opacity-60'}`}>
                                                <span className="text-lg">😊</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.shares_stationery?.term2 === "Rarely" ? 'bg-blue-400 ring-2 ring-white' : 'bg-blue-400 opacity-60'}`}>
                                                <span className="text-lg">😐</span>
                                            </span>
                                            <span
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${peerAssessment?.shares_stationery?.term2 === "Sometimes" ? 'bg-yellow-400 ring-2 ring-white' : 'bg-yellow-400 opacity-60'}`}>
                                                <span className="text-lg">🙂</span>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Toy house decoration - bottom left */}
            <div className="absolute bottom-8 left-10 z-10">
                <div className="relative w-24 h-24">
                    {/* House body */}
                    <div className="absolute bottom-0 left-0 w-20 h-16 bg-amber-300 rounded-sm"></div>
                    {/* Roof */}
                    <div
                        className="absolute bottom-16 left-0 w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-b-[14px] border-b-red-500"></div>
                    {/* Window */}
                    <div
                        className="absolute bottom-8 left-8 w-6 h-6 bg-white border border-gray-300 rounded-sm"></div>
                    {/* Door */}
                    <div className="absolute bottom-0 left-3 w-5 h-8 bg-red-500"></div>
                    {/* Chimney */}
                    <div className="absolute bottom-20 left-14 w-3 h-5 bg-gray-500"></div>
                </div>
            </div>

            {/* Ladder decoration - bottom right */}
            <div className="absolute bottom-6 right-12 z-10">
                <svg viewBox="0 0 60 80" className="w-16 h-20">
                    <line x1="15" y1="5" x2="15" y2="75" stroke="#f97316" strokeWidth="4" />
                    <line x1="45" y1="5" x2="45" y2="75" stroke="#f97316" strokeWidth="4" />
                    <line x1="15" y1="15" x2="45" y2="15" stroke="#f97316" strokeWidth="3" />
                    <line x1="15" y1="30" x2="45" y2="30" stroke="#f97316" strokeWidth="3" />
                    <line x1="15" y1="45" x2="45" y2="45" stroke="#f97316" strokeWidth="3" />
                    <line x1="15" y1="60" x2="45" y2="60" stroke="#f97316" strokeWidth="3" />
                </svg>
            </div>

            {/* Page number */}
            <div className="absolute bottom-4 right-8 font-bold text-blue-800 text-2xl">
                9
            </div>
        </div>
    );
};

// Add required styles for animations
const assessmentStyles = document.createElement('style');

assessmentStyles.textContent = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @media print {
    .bg-sky-200 { background-color: #bae6fd !important; }
    .bg-red-200 { background-color: #fecaca !important; }
    .bg-amber-200 { background-color: #fde68a !important; }
    .bg-red-400 { background-color: #f87171 !important; }
    .bg-blue-400 { background-color: #60a5fa !important; }
    .bg-yellow-400 { background-color: #facc15 !important; }
    .text-blue-800 { color: #1e40af !important; }
  }
`;

document.head.appendChild(assessmentStyles);


export default FoundationalReport;